'use client';
// Paramétrage Qualité — FENÊTRE FLOTTANTE à sections, même rangement que le
// centre de paramétrage du manuel : barre latérale groupée, contenu à droite,
// pied « Annuler / Enregistrer » avec témoin de modifications.
//
// Le paramétrage se superpose à l'écran courant (l'écran Qualité reste derrière)
// au lieu d'occuper une vue à part entière.
//
// Ce qui est COMMUN à la suite (dénomination, logo, couleurs, comptes) n'est
// pas ici : il se règle dans le module Site.
import { ReglagesAliquots } from './ressources/ReglagesAliquots';
import { LIBELLES_PROFILS } from '@/shared/profilsLibelles';
import React, { useEffect, useMemo, useState } from 'react';
import { enTeteCollant } from '@/shared/Tableau';
import { BoutonJournal } from '@/components/BoutonJournal';
import { useGed } from '@/lib/store';
import { fetchEcriture } from '@/lib/ecrire';
import { ReglesRh, JoursFeries, RegimesSpeciaux } from '@/components/screens/RhParametres';
import { PlanLabo } from '@/components/screens/PlanLabo';
/* Le catalogue des règles du CONTRÔLE INTERNE, rapatrié du bas de l'écran des
   cartes le 24/08/2026. Il porte sa propre garde (`cqiRegles`) et son propre
   enregistrement — c'est un onglet AUTONOME, cf. `ongletRh` plus bas. */
import { CatalogueRegles } from '@/components/screens/ressources/CatalogueRegles';
import { fmtDateTime,
  CIRCUIT_STEPS, CODIF_DEFAULT, CODIF_SEGMENTS, DEFAULT_SEGMENTS, autoDiscCode, genCodePreview, procCodif, SEUILS_DEFAUT,
  CODIF_REGISTRES_DEFAUT, REGISTRES_CODIFIES, apercuRefRegistre, ORG_REFS_DEFAUT, jourIso } from '@/lib/meta';
import { peut, peutOuRepli, DROITS_CQ } from '@/lib/meta';
import type { CodifSegment } from '@/lib/meta';
import { VOILE, Skeleton, I, inputStyle, useEscape, useConfirmation, ChampRecherche, COUCHES } from '@/components/ui';
import type { GedSettings } from '@/lib/types';

import { SECTIONS, type Tab } from '@/lib/onglets-parametrage';
import { ReglesParametrage } from '@/components/screens/ReglesParametrage';
import { OngletUtilisateurs, OngletPolitiqueComptes } from '@/components/screens/Utilisateurs';
import { Deploiement } from '@/components/screens/Deploiement';
import { RolesAcces } from '@/components/screens/RolesAcces';
import { ActesReglables } from '@/components/screens/ActesReglables';
import { ReglesNotif } from '@/components/screens/settings/ReglesNotif';
import { ApercuCartouche } from '@/components/screens/settings/ApercuCartouche';
import { MentionsSituation } from '@/components/screens/settings/MentionsSituation';
import { valeursExemple } from '@/components/screens/settings/exempleCartouche';

import { Bouton } from '@/shared/Bouton';
const lbl: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 5 };

/** Même vocabulaire que le circuit léger lui-même (organigramme, cartographie
 *  des processus, fiches de fonction) — brouillon / à vérifier / approuvée. */
const ETAT_CIRCUIT_LEGER_LABEL: Record<string, string> = {
  brouillon: 'brouillon', averifier: 'à vérifier', approuvee: 'approuvée',
};
const ETAT_CIRCUIT_LEGER: Record<string, { bg: string; fg: string }> = {
  brouillon: { bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
  averifier: { bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  approuvee: { bg: 'var(--green-50)', fg: 'var(--green-600)' },
};


function Panel({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ width: 8, height: 8, borderRadius: 'var(--radius-xs)', background: 'var(--color-primary)', flex: 'none' }} />
        <h2 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-strong)' }}>{title}</h2>
      </div>
      {note && <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '7px 0 0', lineHeight: 1.55 }}>{note}</p>}
      <div style={{ marginTop: 16 }}>{children}</div>
    </section>
  );
}

export function SettingsModal() {
  const { settings, saveSettings, session, settingsOpen, closeSettings, naviguerVers } = useGed();
  // Brouillon local : `null` tant que rien n'a été touché — on affiche alors
  // l'état serveur tel quel. Aucune mutation en place (chaque édition
  // reconstruit les objets), donc le store n'est jamais modifié avant
  // l'enregistrement.
  const [edited, setDraft] = useState<GedSettings | null>(null);
  /* LA COPIE LUE À LA PREMIÈRE MODIFICATION (14/10/2026). Le magasin peut être
     rafraîchi pendant que la fenêtre est ouverte ; c'est contre CETTE copie
     qu'on calcule ce qui a changé, et ce sont SES versions qui partent — sans
     quoi une fenêtre périmée réécrivait en silence les rôles et les droits
     attribués entre-temps ailleurs. */
  const baseRef = React.useRef<GedSettings | null>(null);
  // L'onglet demandé par l'appelant — la recherche générale, ou un renvoi
  // depuis un écran. Il n'est lu qu'à l'OUVERTURE : ensuite c'est l'utilisateur
  // qui navigue, et une demande qui persisterait le ramènerait de force sur le
  // même onglet à chaque rendu.
  const tabDemande = useGed((e) => e.settingsTab);
  const [tab, setTab] = useState<Tab | null>((tabDemande as Tab) || null);
  /* ⚠️ CETTE SYNCHRONISATION EST INDISPENSABLE, et son absence rendait la
     demande d'onglet INOPÉRANTE depuis toujours.
     `useState(...)` ne lit sa valeur initiale qu'au MONTAGE. Or cette fenêtre
     ne se démonte jamais — le `if (!settingsOpen) return null` est placé après
     les hooks, comme les règles de React l'imposent. Elle se montait donc au
     démarrage de l'application, alors qu'aucun onglet n'était demandé, et
     `tab` restait `null` pour le reste de la session : toute ouverture ciblée
     retombait sur « Types & circuit ».
     Personne ne l'avait vu parce que le symptôme n'est pas une panne — la
     fenêtre s'ouvre, simplement pas où on l'attendait. Constaté le 24/08/2026
     en vérifiant le renvoi depuis l'écran des cartes CQI.
     On ne réagit qu'au PASSAGE à l'état ouvert, pas à chaque rendu, sans quoi
     l'utilisateur ne pourrait plus quitter l'onglet demandé. */
  const etaitOuvert = React.useRef(settingsOpen);
  useEffect(() => {
    if (settingsOpen && !etaitOuvert.current) setTab((tabDemande as Tab) || null);
    etaitOuvert.current = settingsOpen;
  }, [settingsOpen, tabDemande]);
  const [saving, setSaving] = useState(false);
  const draft = edited ?? settings;

  // Tout ce qui suit doit rester AU-DESSUS du retour anticipé : un hook posé
  // après ne s'exécuterait qu'à l'ouverture de la fenêtre, et React refuse de
  // rendre dans ce cas (« Rendered more hooks than during the previous
  // render »). D'où le calcul de `dirty` ici plutôt qu'au milieu du corps :
  // c'est lui dont dépend le gestionnaire de fermeture.
  const officeCourant = draft?.office ?? OFFICE_DEFAUT;
  const imapNcCourant = draft?.imapNc ?? IMAP_NC_DEFAUT;
  const imapDemCourant = draft?.imapDemandes ?? IMAP_DEMANDES_DEFAUT;
  const dirty = !!edited && !!settings
    && JSON.stringify({ ...draft, office: officeCourant, imapNc: imapNcCourant, imapDemandes: imapDemCourant })
    !== JSON.stringify({
      ...settings, office: settings.office ?? OFFICE_DEFAUT,
      imapNc: settings.imapNc ?? IMAP_NC_DEFAUT,
      imapDemandes: settings.imapDemandes ?? IMAP_DEMANDES_DEFAUT,
    });

  // Fermer remet le brouillon à zéro : la prochaine ouverture repart de l'état
  // serveur, pas des modifications abandonnées.
  const discard = React.useCallback(() => { setDraft(null); baseRef.current = null; closeSettings(); }, [closeSettings]);
  // Échap emprunte la même garde « modifications non enregistrées » que la croix.
  const { demander, confirmation } = useConfirmation();
  const attemptClose = React.useCallback(async () => {
    if (dirty && !(await demander('Des modifications non enregistrées seront perdues. Fermer sans enregistrer ?', { confirmer: 'Fermer sans enregistrer' }))) return;
    discard();
  }, [dirty, discard, demander]);
  useEscape(settingsOpen, attemptClose);
  /* Sur téléphone, le menu est une bande qui défile de côté (globals.css,
     `.ged-params-nav`) : l'onglet ouvert peut s'y trouver hors de l'écran —
     « Utilisateurs », ouvert depuis le menu du profil, est le neuvième. On le
     ramène dans la bande. Sur ordinateur, la colonne le montre déjà : rien ne
     bouge. Déclaré AVANT le `return null` : un crochet après lui changerait
     d'ordre d'un rendu à l'autre (« Rendered more hooks », 19/10/2026). */
  /* Sans dépendances : le menu n'apparaît qu'une fois le paramétrage chargé,
     après le premier rendu. On recentre une seule fois par onglet ouvert. */
  const navRef = React.useRef<HTMLElement>(null);
  const ongletRecentre = React.useRef<HTMLElement | null>(null);
  useEffect(() => {
    const nav = navRef.current;
    const actif = nav?.querySelector<HTMLElement>('[aria-current="page"]') ?? null;
    if (!nav || !actif || actif === ongletRecentre.current) return;
    ongletRecentre.current = actif;
    if (nav.scrollWidth > nav.clientWidth + 2) {
      const a = actif.getBoundingClientRect(); const n = nav.getBoundingClientRect();
      nav.scrollLeft += a.left - n.left - (n.width - a.width) / 2;
    }
  });

  if (!settingsOpen) return null;

  const isRQ = session.roles.includes('rq');
  const estRh = session.roles.includes('rh');
  // Un groupe n'apparaît que si l'utilisateur porte son rôle. Un technicien
  // n'en porte aucun : il n'a rien à régler, et la fenêtre le lui dit.
  /* ⚠️ TROIS axes, pas deux. Le groupe « Contrôle interne » se gouverne par la
     PERMISSION `cqiRegles`, portée par le profil (responsable, super-admin), et
     non par un rôle documentaire : un responsable de paillasse règle Westgard
     sans avoir à porter `rq`, qui lui ouvrirait tout le circuit documentaire.
     Le rabattre sur `isRQ` lui aurait retiré un accès qu'il avait déjà depuis
     l'écran des cartes. */
  const peutCqi = peut(settings?.perms, session.profile, 'cqiRegles');
  /* ⚠️ `superadmin` se lit sur le PROFIL du compte et jamais sur les rôles
     documentaires : c'est ce qui fait qu'un responsable qualité ne voit pas ce
     groupe. Le masquage d'un module retire la rubrique à tout le monde, RQ
     compris — la décision est d'un autre ordre que le paramétrage qualité. */
  const estSuperadmin = session.profile === 'superadmin';
  const sections = SECTIONS.filter((sec) => (
    sec.role === 'rq' ? isRQ
      : sec.role === 'cqi' ? peutCqi
        : sec.role === 'superadmin' ? estSuperadmin
          : estRh));
  // Onglet courant : le premier auquel l'utilisateur a droit. Rester sur
  // « Types & circuit » pour un gestionnaire RH afficherait un panneau vide.
  const ongletsOuverts = sections.flatMap((sec) => sec.items.map((it) => it.tab));
  const tabCourant: Tab = tab && ongletsOuverts.includes(tab) ? tab : (ongletsOuverts[0] ?? 'types');
  // Onglets AUTONOMES : leur contenu ne vit pas dans le JSON `ged-setting`
  // mais dans un registre à part (règles RH, référentiel des emplacements).
  // Ils portent donc leur propre bouton d'enregistrement, et le pied de la
  // fenêtre ne doit ni proposer « Enregistrer », ni annoncer « Aucune
  // modification » — ce qui ferait croire qu'un emplacement créé n'a pas été
  // pris en compte.
  const ongletRh = tabCourant === 'rhregles' || tabCourant === 'rhferies' || tabCourant === 'rhregimes'
    || tabCourant === 'emplacements'
    // Les deux onglets du contrôle interne vivent dans leurs propres registres
    // (`ged-regle-cqi`, et un rapprochement recalculé à chaque lecture) : ils
    // s'enregistrent eux-mêmes, le pied de la fenêtre n'a rien à proposer.
    || tabCourant === 'cqiregles'
    /* Le DÉPLOIEMENT (08/09/2026) écrit par sa propre route, rubrique par
       rubrique, et chaque manœuvre prend effet immédiatement. Sans cette
       ligne, le pied annoncerait « Aucune modification » juste après qu'une
       rubrique a été fermée — et proposerait un « Enregistrer » qui n'aurait
       rien à enregistrer. */
    || tabCourant === 'deploiement' || tabCourant === 'actes'
    /* ── Les trois panneaux rapatriés du Site le 30/08/2026 (corrigé le
       10/09). Ils écrivent eux aussi par leurs propres routes — `/api/team/*`,
       `/api/politique` — mais avaient été oubliés
       ici : leur pied affichait « Aucune modification » et un « Enregistrer »
       inerte JUSTE APRÈS qu'une invitation soit partie ou qu'une matrice ait
       été appliquée. Leur texte d'aide prévenait, ce qui montre que le défaut
       avait été vu ; l'avertir en toutes lettres ne remplace pas le fait de ne
       pas afficher un bouton qui ne sert à rien. */
    || tabCourant === 'utilisateurs' || tabCourant === 'politique';
  // L'en-tête décrit ce que l'utilisateur voit réellement. Annoncer le circuit
  // documentaire à un gestionnaire RH qui n'a que ses deux onglets le ferait
  // chercher des réglages qui ne s'afficheront jamais. Écrit une seule fois :
  // la fenêtre et sa boîte de confirmation partagent le même en-tête.
  const surtitre = isRQ ? 'Centre de paramétrage · Responsable Qualité' : 'Centre de paramétrage · Plannings & RH';
  const titre = isRQ ? 'Paramétrage Qualité' : 'Paramétrage Plannings & RH';
  const chapo = isRQ
    ? <>Circuit documentaire, disciplines, rôles et accès, mise en forme des documents maîtrisés. Les comptes se règlent à l’onglet <strong>Utilisateurs</strong>.</>
    : <>Règles du temps de travail et calendrier des jours fériés. Ces réglages décident de chiffres opposables à un salarié : congés, heures et majorations en découlent.</>;

  const frame = (body: React.ReactNode, footer?: React.ReactNode) => (
    /* ⚠️ Le voile appelait `discard` : un clic à côté JETAIT le brouillon des
       réglages sans un mot. Il est désormais inerte (règle du 04/09/2026). */
    <div style={{ position: 'fixed', inset: 0, zIndex: COUCHES.MODALE, background: VOILE, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 1000, maxHeight: '90vh', background: 'var(--surface-card)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, padding: '20px 26px 16px', borderBottom: '1px solid var(--border-default)' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--color-primary)', marginBottom: 5 }}>{surtitre}</div>
            <h2 style={{ margin: 0, fontSize: 21, fontWeight: 700, color: 'var(--text-strong)' }}>{titre}</h2>
            <p className="ged-params-chapo" style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: 640 }}>
              {chapo}
            </p>
          </div>
          <button onClick={discard} aria-label="Fermer" style={{ flex: 'none', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}>
            <I n="x" s={22} />
          </button>
        </div>
        {body}
        {footer}
      </div>
    </div>
  );

  if (!sections.length) {
    return frame(
      <div style={{ padding: 26, fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
        Aucun réglage ne vous est ouvert. Le paramétrage documentaire relève du Responsable Qualité,
        les règles du temps de travail du gestionnaire RH.
      </div>,
    );
  }
  if (!draft) {
    // Même gabarit d'attente que partout ailleurs (24/08/2026).
    return frame(<div style={{ padding: 26 }}><Skeleton rows={4} /></div>);
  }

  const set = (patch: Partial<GedSettings>) => {
    if (!edited) baseRef.current = draft;
    setDraft({ ...draft, ...patch });
  };
  // Le paramétrage bureautique peut manquer sur une base antérieure : on part
  // des mêmes valeurs par défaut que le serveur plutôt que d'un objet vide.
  // (`dirty` est calculé plus haut, avec le gestionnaire de fermeture.)
  const office = officeCourant;

  const save = async () => {
    setSaving(true);
    const candidat: Partial<GedSettings> = {
      docTypes: draft.docTypes, disciplines: draft.disciplines, gedUsers: draft.gedUsers,
      branding: draft.branding, notif: draft.notif, sites: draft.sites, activeSite: draft.activeSite,
      codification: draft.codification, office, imapNc: draft.imapNc,
      // `releveDemandesEtat` n'est JAMAIS renvoyé : c'est un compte rendu écrit
      // par le serveur, pas un réglage. Le réémettre depuis l'écran écraserait
      // le résultat de la relève par la photographie qu'en avait le navigateur.
      imapDemandes: draft.imapDemandes,
      materiauValidites: draft.materiauValidites,
      quizSatisfaction: draft.quizSatisfaction,
      // Droits par section (lot N6). Le serveur revalide (aucun droit à un
      // profil externe) et journalise chaque écart.
      perms: draft.perms,
      // La grille des rôles (volet « Rôles » de Rôles & accès) : oubliée ici,
      // elle se modifiait à l'écran et la fenêtre se fermait sans rien écrire.
      // Le serveur la réserve au RQ / super admin et en revalide la forme.
      rolesAcces: draft.rolesAcces,
    };
    /* On n'envoie QUE les réglages modifiés : un réglage renvoyé tel quel
       écrasait ce qu'un autre y avait changé depuis l'ouverture. */
    const base = baseRef.current ?? settings;
    const baseNormalisee: Partial<GedSettings> = base ? {
      ...base, office: base.office ?? OFFICE_DEFAUT,
    } : {};
    const modifies = Object.fromEntries(Object.entries(candidat).filter(([k, v]) =>
      JSON.stringify(v) !== JSON.stringify((baseNormalisee as Record<string, unknown>)[k]))) as Partial<GedSettings>;
    const ok = Object.keys(modifies).length
      ? await saveSettings(modifies, base?.versions)
      : true;
    setSaving(false);
    if (ok) discard();
  };

  return (<>
    <div style={{ position: 'fixed', inset: 0, zIndex: COUCHES.MODALE, background: VOILE, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      {/* Hauteur FIXE (et non maxHeight) : la fenêtre garde la même taille quel
          que soit l'onglet — le contenu défile dans sa zone (cf. overflow:auto
          plus bas). Sur petit écran, on retombe sur 90vh. */}
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 1000, height: 'min(760px, 90vh)', background: 'var(--surface-card)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* En-tête */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, padding: '20px 26px 16px', borderBottom: '1px solid var(--border-default)' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--color-primary)', marginBottom: 5 }}>{surtitre}</div>
            <h2 style={{ margin: 0, fontSize: 21, fontWeight: 700, color: 'var(--text-strong)' }}>{titre}</h2>
            <p className="ged-params-chapo" style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: 640 }}>
              {chapo}
            </p>
          </div>
          {/* Ce qui est tracé se VOIT (24/08/2026) : les remises à zéro et
              actes du paramétrage s'écrivaient sous « PARAMETRAGE-QUALITE »
              sans accès à l'écran. */}
          <BoutonJournal reference="PARAMETRAGE-QUALITE" quoi="du paramétrage" />
          <button onClick={attemptClose} aria-label="Fermer" style={{ flex: 'none', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}>
            <I n="x" s={22} />
          </button>
        </div>

        {/* Corps : barre latérale + contenu */}
        <div className="ged-params-body" style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <nav ref={navRef} className="ged-params-nav" style={{ flex: 'none', width: 232, borderInlineEnd: '1px solid var(--border-default)', background: 'var(--gray-25)', overflowY: 'auto', padding: '14px 12px' }}>
            {sections.map((sec) => (
              <div key={sec.group} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 8px 7px', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>
                  <I n={sec.icon} s={13} sw={2} />{sec.group}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {sec.items.map((it) => {
                    const on = tabCourant === it.tab;
                    return (
                      <button key={it.tab} onClick={() => setTab(it.tab)} aria-current={on ? 'page' : undefined} style={{
                        display: 'flex', alignItems: 'center', textAlign: 'start',
                        fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: on ? 700 : 500,
                        color: on ? 'var(--color-primary)' : 'var(--text-body)',
                        background: on ? 'var(--cyan-50)' : 'transparent', border: 'none',
                        borderRadius: 'var(--radius-sm)', padding: '8px 10px', cursor: 'pointer',
                      }}>{it.label}</button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          <div style={{ flex: 1, minWidth: 0, overflow: 'auto', padding: '22px 26px' }}>
            {tabCourant === 'types' && (
              <Panel title="Types de documents & circuit de validation" note="Le circuit s'adapte au type : Rédaction et Approbation sont verrouillées ; Vérification, Signature et Diffusion sont activables.">
                <div style={{ display: 'grid', gap: 8 }}>
                  {draft.docTypes.map((t, i) => (
                    <div key={t.code} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '9px 12px', background: t.active ? 'var(--surface-sunken)' : 'var(--gray-25)', borderRadius: 'var(--radius-md)', opacity: t.active ? 1 : 0.6 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 'var(--radius-xs)', background: t.color, flex: 'none' }} />
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--navy-900)', minWidth: 30 }}>{t.code}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', flex: '1 1 140px' }}>{t.label}</span>
                      <span style={{ display: 'inline-flex', gap: 5 }}>
                        {CIRCUIT_STEPS.map((s) => {
                          const on = t.steps.includes(s.key);
                          return (
                            <button key={s.key} disabled={!!s.locked} onClick={() => {
                              const docTypes = draft.docTypes.map((x, j) => j !== i ? x : { ...x, steps: on ? x.steps.filter((k) => k !== s.key) : [...x.steps, s.key] });
                              set({ docTypes });
                            }} title={s.label} style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-sm)', padding: '4px 8px', cursor: s.locked ? 'default' : 'pointer', border: `1px solid ${on ? 'var(--cyan-200)' : 'var(--border-default)'}`, background: on ? 'var(--cyan-50)' : 'var(--surface-card)', color: on ? 'var(--cyan-700)' : 'var(--text-muted)' }}>
                              {s.short}
                            </button>
                          );
                        })}
                      </span>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-muted)', cursor: 'pointer' }}>
                        {/* Le libellé visible ne dit que « actif » : répété à
                            chaque ligne, il ne permettait pas de savoir de quel
                            type de document il s'agit hors du contexte visuel. */}
                        <input type="checkbox" checked={t.active} aria-label={`Type ${t.label || t.code} actif`}
                          onChange={(e) => set({ docTypes: draft.docTypes.map((x, j) => j !== i ? x : { ...x, active: e.target.checked }) })} style={{ accentColor: 'var(--color-primary)' }} />
                        actif
                      </label>
                    </div>
                  ))}
                </div>
                {/* ── LES DOCUMENTS À CIRCUIT LÉGER (05/09/2026) ───────────────
                    « Je veux voir TOUS les types de documents, y compris les
                    organigrammes et le processus » : ils n'étaient nulle part
                    dans cet écran, alors qu'ils suivent depuis les Lots 4-6 un
                    vrai circuit (brouillon → à vérifier → approuvée). Ils ne
                    rejoignent PAS le tableau ci-dessus — leurs trois états ne
                    sont pas les cinq étapes rédaction/vérification/
                    approbation/signature/diffusion, et il n'existe qu'UN SEUL
                    exemplaire de chacun (pas une instance par document créé) :
                    les y mélanger ferait croire à des réglages qui n'existent
                    pas (activer/désactiver, cocher « signature »). Cette carte
                    les NOMME, en lecture seule — la source reste le circuit
                    lui-même (organigramme, cartographie des processus). */}
                <div style={{ marginTop: 18, padding: '13px 15px', background: 'var(--gray-25)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
                  <div style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.55, marginBottom: 10 }}>
                    Trois documents du SMQ suivent un <strong>circuit plus léger</strong> — brouillon → à
                    vérifier → approuvée, sans rédacteur ni vérificateur nommés par exemplaire — et n’entrent
                    donc pas dans le tableau ci-dessus. Ce sont les seuls de leur genre : il n’en existe qu’un
                    exemplaire de chaque, pas une instance par document créé.
                  </div>
                  <div style={{ display: 'grid', gap: 6 }}>
                    {([
                      ['BIO24-A-ORG-02', 'Organigramme nominatif', draft.codification?.orgCircuit?.nominatif],
                      ['BIO24-A-ORG-01', 'Organigramme fonctionnel', draft.codification?.orgCircuit?.fonctionnel],
                      ['—', 'Cartographie des processus', draft.codification?.orgCircuit?.cartographie],
                    ] as const).map(([ref, label, circuit]) => (
                      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '7px 10px', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-faint)', minWidth: 100 }}>{ref}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', flex: '1 1 200px' }}>{label}</span>
                        <span style={{
                          fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '3px 9px',
                          background: (ETAT_CIRCUIT_LEGER[circuit?.etat || 'approuvee']).bg,
                          color: (ETAT_CIRCUIT_LEGER[circuit?.etat || 'approuvee']).fg,
                        }}>
                          v{circuit?.version || '01'} · {ETAT_CIRCUIT_LEGER_LABEL[circuit?.etat || 'approuvee']}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </Panel>
            )}

            {tabCourant === 'disciplines' && (
              <Panel
                title="Disciplines — les familles biologiques"
                note="Une discipline est ce que le laboratoire ANALYSE. C’est le même référentiel que la famille des fiches du catalogue : une discipline racine est une famille, une discipline rattachée est une sous-famille. Le « + » d’un nœud ajoute une sous-famille sous lui."
              >
                <ArbreDisciplines
                  disciplines={draft.disciplines}
                  processus={(draft.processus || []).filter((p) => p.active !== false).map((p) => p.name)}
                  onChange={(disciplines) => set({ disciplines })}
                />
              </Panel>
            )}

            {tabCourant === 'sites' && (
              <Panel
                title="Sites du laboratoire"
                note="Les implantations physiques du laboratoire. Cette liste alimente le champ « Site » proposé à la création d’un document, et reçoit une abréviation à l’onglet Codification."
              >
                <SitesPanel draft={draft} onSet={set} />
              </Panel>
            )}

            {tabCourant === 'codification' && (
              <Panel title="Codification des références documentaires" note="Modèle du code attribué à un nouveau document : préfixe du type, code de discipline, puis numéro séquentiel. Change les codes des PROCHAINS documents — les documents déjà créés gardent le leur.">
                <CodificationPanel draft={draft} onSet={set} />
              </Panel>
            )}

            {tabCourant === 'acces' && (
              <Panel
                title="Rôles & accès"
                note="Qui porte quels rôles, ce que chaque rôle ouvre, et les cinq règles qui s’appliquent par-dessus. Un seul endroit pour les droits : les rôles d’une personne et la grille s’enregistrent avec le bouton « Enregistrer » au pied de la fenêtre."
              >
                <RolesAcces draft={draft} set={set} dirty={dirty} />
              </Panel>
            )}

            {tabCourant === 'droitscq' && (
              <Panel
                title="Droits du contrôle qualité"
                note="Les douze actes du contrôle qualité (saisir, valider, déroger, approuver une cible, imprimer…), réglés par type de compte. ⚠️ C’est le dernier réglage par type de compte : les rubriques, elles, s’ouvrent par les rôles (onglet Rôles & accès)."
              >
                <DroitsCqPanel perms={draft.perms || {}} onChange={(perms) => set({ perms })} />
              </Panel>
            )}

            {/* ── Rapatriés du module Site le 30/08/2026 ────────────────────
                ⚠️ Ces trois panneaux ne passent PAS par `draft` / `set` comme
                les autres : ils écrivent directement par leurs propres routes
                (`/api/team/*`, `/api/politique`). Les
                faire transiter par le brouillon du paramétrage aurait mis
                l'annuaire des comptes derrière le bouton « Enregistrer » global
                — une invitation partie ne se retire pas d'un brouillon. */}
            {tabCourant === 'utilisateurs' && (
              <Panel
                title="Utilisateurs"
                note="L’annuaire des comptes de la suite — site, catalogue et Qualité. Les actes prennent effet immédiatement : ils ne passent pas par le bouton « Enregistrer » de ce paramétrage. ⚠️ Ce n’est pas l’annuaire de l’EIL, qui a sa propre base et ses propres comptes depuis le 29/08/2026."
              >
                <OngletUtilisateurs />
              </Panel>
            )}

            {tabCourant === 'politique' && (
              <Panel
                title="Politique des comptes"
                note="Deux réglages COMMUNS aux trois applications : à partir de quand un compte est signalé dormant, et au bout de combien de temps une session inactive se ferme. Ils se réglaient depuis le module Site jusqu’au 30/08/2026."
              >
                <OngletPolitiqueComptes />
              </Panel>
            )}

            {tabCourant === 'actes' && (
              <Panel
                title="Actes réservés"
                note="Ce qu’on peut faire d’EXCEPTIONNEL dans une rubrique par ailleurs ouverte : rouvrir un dossier scellé, supprimer une ligne d’un registre, approuver, reconstruire un planning. Le travail ordinaire d’une rubrique suit la casquette et n’est pas ici. ⚠️ Onze familles restent au responsable qualité quoi que vous cochiez — elles sont affichées, verrouillées, avec leur motif."
              >
                <ActesReglables />
              </Panel>
            )}

            {/* LE DÉPLOIEMENT (08/09/2026). ⚠️ Voisin des droits par commodité de
                rangement, mais d'une AUTRE NATURE : il est global, il ferme au
                RQ comme aux autres, et il laisse les droits intacts dessous.
                Le bandeau de l'écran porte cet avertissement — sans lui, la
                liste se lirait comme une matrice de droits de plus, ce que ce
                mécanisme existe précisément pour éviter. */}
            {tabCourant === 'deploiement' && (
              <Panel
                title="Modules mis à disposition"
                note="Choisir les modules que le laboratoire utilise vraiment. Un module masqué disparaît pour TOUT LE MONDE, vous comprise — et les droits restent inchangés dessous, ce qui permet de le remettre d’un seul geste. ⚠️ Réservé au super administrateur : ce n’est pas un réglage de droits, c’est le périmètre de l’outil."
              >
                <Deploiement />
              </Panel>
            )}

            {tabCourant === 'cartouche' && (
              <Panel title="Identité & cartouche documentaire" note="Alimente l'en-tête de l'aperçu officiel et la seconde ligne du pied de page. La mention « copie maîtrisée / non maîtrisée », elle, est écrite par la plateforme : elle nomme l'exemplaire et n'est pas paramétrable.">
                <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
                  <div><label style={lbl}>Nom du laboratoire</label><input value={draft.branding.labName} onChange={(e) => set({ branding: { ...draft.branding, labName: e.target.value } })} style={inputStyle} /></div>
                  <div><label style={lbl}>Mention</label><input value={draft.branding.mention} onChange={(e) => set({ branding: { ...draft.branding, mention: e.target.value } })} style={inputStyle} /></div>
                  <div><label style={lbl}>Référentiel</label><input value={draft.branding.norme} onChange={(e) => set({ branding: { ...draft.branding, norme: e.target.value } })} style={inputStyle} /></div>
                  <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Pied de page</label><input value={draft.branding.footer} onChange={(e) => set({ branding: { ...draft.branding, footer: e.target.value } })} style={inputStyle} /></div>
                </div>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 12 }}>
                  {(['reference', 'version', 'date', 'pagination'] as const).map((k) => (
                    <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-body)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={draft.branding.cartouche[k] !== false} onChange={(e) => set({ branding: { ...draft.branding, cartouche: { ...draft.branding.cartouche, [k]: e.target.checked } } })} style={{ accentColor: 'var(--color-primary)' }} />
                      {({ reference: 'Référence', version: 'Version', date: 'Date', pagination: 'Pagination' } as Record<string, string>)[k]}
                    </label>
                  ))}
                </div>
                {/* « Je veux pouvoir les voir » (RQ, 06/10/2026) : les quatre cases
                    et les quatre champs se lisaient en aveugle — il fallait ouvrir
                    l'aperçu d'un vrai document pour savoir ce qu'ils produisent. */}
                <ApercuCartouche branding={draft.branding} par={session.name} />
                {/* « Je veux pouvoir paramétrer le contenu des cartouches qui sont
                    insérés selon la situation » (RQ, 06/10/2026). Les quatre phrases
                    étaient écrites en dur dans le serveur ; elles se réécrivent ici,
                    avec leurs jetons. Rangé APRÈS l'aperçu : on règle ce qu'on vient
                    de voir. */}
                <MentionsSituation
                  mentions={draft.branding.mentions}
                  valeurs={valeursExemple(draft.branding.labName, session.name)}
                  onChange={(mentions) => set({ branding: { ...draft.branding, mentions } })} />
              </Panel>
            )}

            {tabCourant === 'office' && (
              <Panel title="Documents bureautiques (OnlyOffice)" note="Police, styles de titres et trame appliqués aux fichiers Word créés depuis la GED. Sans effet sur les fichiers déjà créés ou importés.">
                <OfficeParams value={office} onChange={(o) => set({ office: o })} />
              </Panel>
            )}

            {tabCourant === 'notif' && (
              <Panel title="Notifications & rappels" note="Qui est prévenu quand quelque chose arrive du site, relances d'accusés de lecture, alertes de revue et synthèse périodique.">
                {/* ── QUI EST PRÉVENU, EN PREMIER (06/10/2026) ───────────────
                    Rangé AVANT les délais de relance, et pas par goût de la
                    nouveauté : les délais règlent la cadence de rappels qui
                    partent déjà, tandis que ces règles décident si quelque chose
                    part DU TOUT. Un onglet qui ouvrait sur « relance après
                    combien de jours » laissait croire que la question du
                    destinataire était réglée ailleurs — elle ne l'était nulle
                    part. */}
                <div style={{ marginBottom: 22, paddingBottom: 18, borderBottom: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', marginBottom: 8 }}>
                    Qui est prévenu quand quelque chose arrive du site
                  </div>
                  {/* ⚠️ `...draft.notif` dans les DEUX rappels : `onChange` du
                      paramétrage REMPLACE l'objet entier. N'écrire que
                      `{ regles }` effacerait les délais de lecture et les
                      seuils d'ancienneté à chaque modification — le piège
                      rencontré le 25/08 sur le quiz de satisfaction. */}
                  <ReglesNotif
                    value={draft.notif.regles}
                    onChange={(regles) => set({ notif: { ...draft.notif, regles } })}
                    familles={draft.notif.familles}
                    onChangeFamilles={(familles) => set({ notif: { ...draft.notif, familles } })} />
                </div>

                {/* Délai de lecture — l'ISO 15189 §8.3 ne chiffre rien : elle
                    demande que le personnel ait pris connaissance des documents
                    applicables AVANT de les appliquer. Le laboratoire fixe donc
                    son propre délai, et c'est ici qu'il l'écrit. */}
                <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', marginBottom: 16 }}>
                  <div>
                    <label style={lbl}>Délai de lecture (jours)</label>
                    <input type="number" min={1} max={365} value={draft.notif.delaiLectureJ ?? 15}
                      onChange={(e) => set({ notif: { ...draft.notif, delaiLectureJ: Math.max(1, Math.min(365, +e.target.value || 15)) } })} style={inputStyle} />
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5, lineHeight: 1.45 }}>
                      Compté à partir de l’émission de la diffusion. Modifier ce délai n’affecte que les diffusions <strong>à venir</strong> : une échéance déjà annoncée à un lecteur ne bouge pas.
                    </div>
                  </div>
                  <div>
                    <label style={lbl}>Délai si en formation (jours)</label>
                    <input type="number" min={1} max={365} value={draft.notif.delaiLectureFormationJ ?? 30}
                      onChange={(e) => set({ notif: { ...draft.notif, delaiLectureFormationJ: Math.max(1, Math.min(365, +e.target.value || 30)) } })} style={inputStyle} />
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5, lineHeight: 1.45 }}>
                      Pour qui n’est pas encore habilité sur la discipline du document : il le découvre en formation, il ne l’applique pas seul.
                    </div>
                  </div>
                </div>
                <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
                  <div><label style={lbl}>Relance accusés après (jours)</label><input type="number" value={draft.notif.relanceAck} onChange={(e) => set({ notif: { ...draft.notif, relanceAck: +e.target.value || 0 } })} style={inputStyle} /></div>
                  <div><label style={lbl}>Alerte revue avant (jours)</label><input type="number" value={draft.notif.revueAvant} onChange={(e) => set({ notif: { ...draft.notif, revueAvant: +e.target.value || 0 } })} style={inputStyle} /></div>
                </div>
                {/* Seuils d'ancienneté : ils ne déclenchent pas d'envoi, ils
                    décident à partir de quand un dossier encore ouvert vire à
                    l'ambre dans son registre. Le délai acceptable dépend du
                    laboratoire — d'où leur place ici plutôt qu'en dur. */}
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', marginBottom: 4 }}>Signalement des dossiers qui traînent</div>
                  <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 14px', lineHeight: 1.55 }}>
                    Au-delà de ces délais, une non-conformité ou une réclamation encore ouverte est signalée dans son registre, et devient filtrable d’un clic.
                  </p>
                  <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
                    <div>
                      <label style={lbl} htmlFor="seuil-nc">Non-conformité ouverte depuis (jours)</label>
                      <input id="seuil-nc" type="number" min={1} value={draft.notif.ncAncienneteJ ?? SEUILS_DEFAUT.ncAncienneteJ}
                        onChange={(e) => set({ notif: { ...draft.notif, ncAncienneteJ: +e.target.value || SEUILS_DEFAUT.ncAncienneteJ } })} style={inputStyle} />
                    </div>
                    <div>
                      <label style={lbl} htmlFor="seuil-recl">Réclamation en cours depuis (jours)</label>
                      <input id="seuil-recl" type="number" min={1} value={draft.notif.reclReponseJ ?? SEUILS_DEFAUT.reclReponseJ}
                        onChange={(e) => set({ notif: { ...draft.notif, reclReponseJ: +e.target.value || SEUILS_DEFAUT.reclReponseJ } })} style={inputStyle} />
                    </div>
                  </div>
                </div>
              </Panel>
            )}
            {tabCourant === 'imapnc' && (
              <Panel title="Relève e-mail (NC)"
                note="C’est la SEULE relève automatique de la suite : aucune autre boîte n’est interrogée ailleurs. Tout message qui arrive ici devient une non-conformité « à qualifier » — pas de convention d’objet à retenir par les déclarants.">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!imapNcCourant.active}
                    onChange={(e) => set({ imapNc: { ...imapNcCourant, active: e.target.checked } })}
                    style={{ accentColor: 'var(--color-primary)', width: 16, height: 16 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>Relève active</span>
                </label>
                <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px 16px' }}>
                  <div>
                    <label style={lbl} htmlFor="imap-host">Serveur IMAP</label>
                    <input id="imap-host" dir="ltr" value={imapNcCourant.host} placeholder="imap.exemple.tn"
                      onChange={(e) => set({ imapNc: { ...imapNcCourant, host: e.target.value } })} style={inputStyle} />
                  </div>
                  <div>
                    <label style={lbl} htmlFor="imap-port">Port</label>
                    <input id="imap-port" type="number" value={imapNcCourant.port}
                      onChange={(e) => set({ imapNc: { ...imapNcCourant, port: +e.target.value || 993 } })} style={inputStyle} />
                  </div>
                </div>
                <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', marginTop: 12 }}>
                  <div>
                    <label style={lbl} htmlFor="imap-user">Identifiant</label>
                    <input id="imap-user" dir="ltr" value={imapNcCourant.user}
                      onChange={(e) => set({ imapNc: { ...imapNcCourant, user: e.target.value } })} style={inputStyle} />
                  </div>
                  <div>
                    <label style={lbl} htmlFor="imap-dossier">Dossier relevé</label>
                    <input id="imap-dossier" dir="ltr" value={imapNcCourant.dossier} placeholder="INBOX"
                      onChange={(e) => set({ imapNc: { ...imapNcCourant, dossier: e.target.value || 'INBOX' } })} style={inputStyle} />
                  </div>
                </div>
                <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px 16px', marginTop: 12 }}>
                  <div>
                    <label style={lbl} htmlFor="imap-pass">
                      Mot de passe {imapNcCourant.motDePasseDefini && !imapNcCourant.motDePasse && <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--green-600)' }}>· déjà enregistré</span>}
                    </label>
                    <input id="imap-pass" type="password" dir="ltr" value={imapNcCourant.motDePasse || ''}
                      placeholder={imapNcCourant.motDePasseDefini ? '••••••••  (laisser vide pour conserver)' : ''}
                      onChange={(e) => set({ imapNc: { ...imapNcCourant, motDePasse: e.target.value } })} style={inputStyle} />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 22, cursor: 'pointer' }}>
                    <input type="checkbox" checked={imapNcCourant.tls !== false}
                      onChange={(e) => set({ imapNc: { ...imapNcCourant, tls: e.target.checked } })}
                      style={{ accentColor: 'var(--color-primary)', width: 16, height: 16 }} />
                    <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>Connexion chiffrée (TLS)</span>
                  </label>
                </div>
                <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px 16px', marginTop: 12 }}>
                  <div>
                    <label style={lbl} htmlFor="imap-depuis">Relever à partir du</label>
                    <input id="imap-depuis" type="date" value={jourIso(imapNcCourant.depuisLe || '')}
                      onChange={(e) => set({ imapNc: { ...imapNcCourant, depuisLe: e.target.value ? `${e.target.value}T00:00:00.000Z` : undefined } })}
                      style={inputStyle} />
                  </div>
                  <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '22px 0 0', lineHeight: 1.5 }}>
                    Date de mise en service, posée toute seule quand vous activez la relève.
                    Les messages arrivés <strong>avant</strong> ne sont pas relevés : sur une boîte
                    qui servait déjà, « non lu » ne veut pas dire « pas encore traité », et tout
                    l’arriéré deviendrait des non-conformités. Reculez la date pour élargir.
                  </p>
                </div>
                <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '16px 0 0', lineHeight: 1.55 }}>
                  Un compte Gmail/Workspace exige un mot de passe d’application, jamais le mot de passe du compte.
                  La relève passe toutes les 5 minutes ; un message non lu qui n’a pas encore produit de NC est retenté au passage suivant.
                </p>
              </Panel>
            )}
            {tabCourant === 'imapdemandes' && (
              <Panel title="Relève e-mail (réponses aux demandes)"
                note="La réponse d’un demandeur arrivait dans la messagerie et nulle part ailleurs : le fil d’une demande s’arrêtait à ce que le laboratoire avait écrit. Cette relève va chercher la moitié manquante. Un message n’est rattaché que s’il cite une référence émise par le laboratoire (BIO24-RDV-…, BIO24-MSG-…, BIO24-CPT-…) ET provient de l’adresse enregistrée sur cette demande — jamais sur la foi d’un nom.">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!imapDemCourant.active}
                    onChange={(e) => set({ imapDemandes: { ...imapDemCourant, active: e.target.checked } })}
                    style={{ accentColor: 'var(--color-primary)', width: 16, height: 16 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>Relève active</span>
                </label>
                <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px 16px' }}>
                  <div>
                    <label style={lbl} htmlFor="imapd-host">Serveur IMAP</label>
                    <input id="imapd-host" dir="ltr" value={imapDemCourant.host} placeholder="imap.exemple.tn"
                      onChange={(e) => set({ imapDemandes: { ...imapDemCourant, host: e.target.value } })} style={inputStyle} />
                  </div>
                  <div>
                    <label style={lbl} htmlFor="imapd-port">Port</label>
                    <input id="imapd-port" type="number" value={imapDemCourant.port}
                      onChange={(e) => set({ imapDemandes: { ...imapDemCourant, port: +e.target.value || 993 } })} style={inputStyle} />
                  </div>
                </div>
                <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', marginTop: 12 }}>
                  <div>
                    <label style={lbl} htmlFor="imapd-user">Identifiant</label>
                    <input id="imapd-user" dir="ltr" value={imapDemCourant.user}
                      onChange={(e) => set({ imapDemandes: { ...imapDemCourant, user: e.target.value } })} style={inputStyle} />
                  </div>
                  <div>
                    <label style={lbl} htmlFor="imapd-dossier">Dossier relevé</label>
                    <input id="imapd-dossier" dir="ltr" value={imapDemCourant.dossier} placeholder="INBOX"
                      onChange={(e) => set({ imapDemandes: { ...imapDemCourant, dossier: e.target.value || 'INBOX' } })} style={inputStyle} />
                  </div>
                </div>
                <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px 16px', marginTop: 12 }}>
                  <div>
                    <label style={lbl} htmlFor="imapd-pass">
                      Mot de passe {imapDemCourant.motDePasseDefini && !imapDemCourant.motDePasse && <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--green-600)' }}>· déjà enregistré</span>}
                    </label>
                    <input id="imapd-pass" type="password" dir="ltr" value={imapDemCourant.motDePasse || ''}
                      placeholder={imapDemCourant.motDePasseDefini ? '••••••••  (laisser vide pour conserver)' : ''}
                      onChange={(e) => set({ imapDemandes: { ...imapDemCourant, motDePasse: e.target.value } })} style={inputStyle} />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 22, cursor: 'pointer' }}>
                    <input type="checkbox" checked={imapDemCourant.tls !== false}
                      onChange={(e) => set({ imapDemandes: { ...imapDemCourant, tls: e.target.checked } })}
                      style={{ accentColor: 'var(--color-primary)', width: 16, height: 16 }} />
                    <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>Connexion chiffrée (TLS)</span>
                  </label>
                </div>
                <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px 16px', marginTop: 12 }}>
                  <div>
                    <label style={lbl} htmlFor="imapd-interv">Intervalle (minutes)</label>
                    <input id="imapd-interv" type="number" min={5} value={imapDemCourant.intervalleMinutes ?? 5}
                      onChange={(e) => set({ imapDemandes: { ...imapDemCourant, intervalleMinutes: Math.max(5, +e.target.value || 5) } })} style={inputStyle} />
                  </div>
                  <div>
                    <label style={lbl} htmlFor="imapd-depuis">Relever à partir du</label>
                    <input id="imapd-depuis" type="date" value={jourIso(imapDemCourant.depuisLe || '')}
                      onChange={(e) => set({ imapDemandes: { ...imapDemCourant, depuisLe: e.target.value ? `${e.target.value}T00:00:00.000Z` : undefined } })}
                      style={inputStyle} />
                    <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 0 0', lineHeight: 1.5 }}>
                      Date de mise en service, posée toute seule à l’activation : l’arriéré non lu
                      d’avant le branchement n’est pas descendu. Reculez la date pour élargir.
                    </p>
                  </div>
                </div>
                <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '16px 0 0', lineHeight: 1.55 }}>
                  Un compte Gmail/Workspace exige un mot de passe d’application, jamais le mot de passe du compte.
                  Aucun message n’est supprimé ni déplacé : seul le drapeau « lu » est posé, et seulement après qu’une réponse a bien été rattachée —
                  ce qui rend une relève interrompue rejouable sans doublon. Un message qu’aucune demande ne réclame est laissé INTACT et non lu,
                  et signalé ci-dessous : c’est à traiter depuis la messagerie.
                </p>
                <EtatReleveDemandes etat={settings?.releveDemandesEtat} />
              </Panel>
            )}
            {tabCourant === 'matvalid' && (
              <Panel title="Validité des matériaux après ouverture"
                note="Combien de jours un calibrant ou un contrôle reste utilisable une fois reconstitué — ou, à défaut, une fois le kit ouvert. C'est une décision du laboratoire : elle ne figure sur aucun registre fournisseur, qui ne donne que la péremption du kit fermé. Elle vaut pour tous les flacons du même produit ; l'échéance de chacun s'en déduit et n'est jamais saisie à la main.">
                <ValiditeMateriaux value={draft.materiauValidites} onChange={(v) => set({ materiauValidites: v })} />
              </Panel>
            )}
            {tabCourant === 'annonces' && (
              <Panel title="Pop-up programmé — annonces au personnel"
                note="Un petit mot (les fêtes), une photo, ou un mini-sondage (l'humeur du jour), affiché UNE fois à chaque personne à l'ouverture de l'application, entre deux dates. Les réponses sont comptées en temps réel, sans être nommées ; les annonces passées restent listées — rien ne se supprime, tout se désactive.">
                <AnnoncesParams />
              </Panel>
            )}
            {tabCourant === 'quiz' && (
              <Panel title="Quiz de satisfaction du site"
                note="Deux à cinq questions posées dans le panneau « Votre avis », après le choix de la nature. Toutes facultatives pour le déposant : le quiz accompagne l'avis, il ne le conditionne jamais. Les réponses s'affichent sur la fiche du retour, avec le libellé du jour du dépôt — reformuler une question ne réécrit pas les retours déjà reçus.">
                <QuizParams value={draft.quizSatisfaction} onChange={(v) => set({ quizSatisfaction: v })} />
              </Panel>
            )}
            {tabCourant === 'rhregles' && (
              <Panel title="Règles du temps de travail"
                note="Droit à congé, report, décompte des jours, période de paie et motifs d'absence. Ces règles décident de chiffres opposables à un salarié : rien n'y est deviné.">
                <ReglesRh />
              </Panel>
            )}
            {tabCourant === 'emplacements' && (
              <Panel title="Plan du laboratoire & stockage"
                note="Un seul référentiel d’endroits, partagé par les équipements, les lignes de stockage et les copies maîtrisées affichées. Il sert à retrouver : où est un appareil, ce que contient une armoire, quels documents sont affichés dans une pièce. Ce qui est rangé sur une étagère s’ajoute et se corrige sur l’étagère même ; les actes prennent effet immédiatement, sans passer par « Enregistrer ».">
                <PlanLabo />
              </Panel>
            )}
            {tabCourant === 'regles' && (
              <Panel title="Règles affichées aux écrans"
                note="Les énoncés que le personnel lit derrière le « i », au point exact où le choix se fait — critères de gravité, ce qui bloque une clôture, ce que la norme n’impose pas. Chacun porte sa version et son approbateur : c’est ce qui permet à l’écran de remplacer la procédure écrite plutôt que de la doubler. Les VALEURS qu’ils citent sont lues dans le moteur et ne se saisissent nulle part : un énoncé ne peut donc pas décrire un seuil qui n’est pas appliqué.">
                <ReglesParametrage />
              </Panel>
            )}
            {tabCourant === 'cqiregles' && (
              <Panel title="Règles d’acceptation du contrôle interne"
                note="Ce que le moteur sait évaluer sur une carte : les six règles classiques de Westgard, et celles que le laboratoire ajoute. Une règle se décrit par cinq paramètres — elle ne demande aucune reprise de code. Désactiver une règle ne réécrit aucun verdict déjà rendu.">
                <CatalogueRegles />
              </Panel>
            )}
            {tabCourant === 'rhferies' && (
              <Panel title="Jours fériés"
                note="Le calendrier qui décide des jours non décomptés des congés et des majorations de férié.">
                <JoursFeries />
              </Panel>
            )}
            {tabCourant === 'rhregimes' && (
              <Panel title="Régimes spéciaux"
                note="Les périodes où les postes ferment — Ramadan, fermeture annuelle, travaux. Elles priment sur les jours fériés, mais une exception datée sur un poste prime sur elles.">
                <RegimesSpeciaux />
              </Panel>
            )}
          </div>
        </div>

        {/* Pied */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '14px 26px', borderTop: '1px solid var(--border-default)', background: 'var(--gray-25)' }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: !ongletRh && dirty ? 'var(--amber-600)' : 'var(--text-muted)' }}>
            {ongletRh
              ? 'Ce panneau s’enregistre depuis son propre bouton, au fil des modifications.'
              : (dirty ? 'Modifications non enregistrées' : 'Aucune modification')}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Bouton taille="sm" onClick={attemptClose}>Fermer</Bouton>
            {!ongletRh && (
              <Bouton variante="primaire" onClick={save} disabled={!dirty || saving}>
                <I n="check" s={15} />{saving ? 'Enregistrement…' : 'Enregistrer'}
              </Bouton>
            )}
          </div>
        </div>
      </div>
    </div>
    {confirmation}
  </>);
}

// ---- Rôles documentaires : liste filtrable (nom, fonction, rôle) ----
type OfficeConf = NonNullable<GedSettings['office']>;

// Mêmes valeurs que le serveur (`OFFICE_DEFAUT` dans office.ts) : les deux
// doivent rester alignés, sinon l'aperçu ment sur le rendu réel.
const OFFICE_DEFAUT: OfficeConf = {
  police: 'Calibri', taille: 11,
  titres: {
    h1: { taille: 16, couleur: '#0B2447', gras: true, majuscules: false },
    h2: { taille: 13, couleur: '#0B2447', gras: true, majuscules: false },
    h3: { taille: 12, couleur: '#334155', gras: true, majuscules: false },
  },
  trameActive: true,
  trame: ['Objet', 'Domaine d’application', 'Documents de référence', 'Responsabilités', 'Description du processus'],
};

type ImapNcConf = NonNullable<GedSettings['imapNc']>;
const IMAP_NC_DEFAUT: ImapNcConf = { active: false, host: '', port: 993, tls: true, user: '', dossier: 'INBOX' };

/** État de la DERNIÈRE relève des réponses — en lecture seule.
 *
 *  Il existe pour une raison unique : une relève qui tombe en panne sans que
 *  personne ne le voie est PIRE que pas de relève, parce que le laboratoire
 *  croit alors ses réponses lues. La RQ n'a pas accès aux journaux du serveur ;
 *  elle a accès à cet écran.
 *
 *  Tout ce qui vient de la boîte (objet, expéditeur) est du contenu d'e-mail,
 *  donc une donnée NON FIABLE : c'est interpolé comme du texte — React échappe —
 *  et jamais rendu comme du balisage. */
function EtatReleveDemandes({ etat }: { etat?: GedSettings['releveDemandesEtat'] }) {
  const cadre: React.CSSProperties = {
    marginTop: 20, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
    padding: '12px 14px', background: 'var(--gray-25)',
  };
  if (!etat?.fin) {
    return (
      <div style={cadre}>
        <div style={{ ...lbl, marginBottom: 6 }}>Dernière relève</div>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
          Aucune relève n’a encore été jouée. Tant que cette ligne l’affiche, rien n’est relevé — et ce n’est pas un défaut d’affichage.
        </p>
      </div>
    );
  }
  const echec = etat.statut === 'echec';
  const quand = (() => {
    const d = new Date(etat.fin as string);
    // `fmtDateTime` de la maison : `toLocaleString` écrivait 24/08/2026 avec
    // des barres quand tout le reste de l'application écrit 24.08.2026.
    return Number.isNaN(d.getTime()) ? String(etat.fin) : fmtDateTime(etat.fin as string);
  })();
  return (
    <div style={{ ...cadre, borderColor: echec ? 'var(--red-300)' : 'var(--border-default)', background: echec ? 'var(--red-50)' : 'var(--gray-25)' }}>
      <div style={{ ...lbl, marginBottom: 6 }}>Dernière relève</div>
      <p style={{ margin: 0, fontSize: 12.5, color: echec ? 'var(--red-600)' : 'var(--text-body)', lineHeight: 1.6 }}>
        <strong>{echec ? 'ÉCHEC' : 'Terminée'}</strong> le {quand} — {etat.rattachees ?? 0} réponse(s) rattachée(s),
        {' '}{etat.nonRattaches ?? 0} non rattaché(s), {etat.echecs ?? 0} échec(s) de traitement.
      </p>
      {etat.erreur && (
        <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--red-600)', lineHeight: 1.55, wordBreak: 'break-word' }}>{etat.erreur}</p>
      )}
      {!!etat.exemples?.length && (
        <div style={{ marginTop: 10 }}>
          <div style={{ ...lbl, marginBottom: 5 }}>Messages non rattachés (laissés non lus dans la boîte)</div>
          <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            {etat.exemples.map((x, i) => (
              <li key={i}>
                <span style={{ color: 'var(--text-body)' }}>{x.objet || '(sans objet)'}</span>
                {x.expediteur ? ` — ${x.expediteur}` : ''}
                {x.motif ? ` — ${x.motif}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

type ImapDemandesConf = NonNullable<GedSettings['imapDemandes']>;
// `active: false` par défaut — comme la boîte des NC : tant que la boîte n'est
// pas renseignée et vérifiée, rien ne se relève. Le silence n'active rien.
const IMAP_DEMANDES_DEFAUT: ImapDemandesConf = {
  active: false, host: '', port: 993, tls: true, user: '', dossier: 'INBOX', intervalleMinutes: 5,
};

// Polices sûres : présentes dans le conteneur OnlyOffice, donc rendues à
// l'identique à l'écran et à l'impression.
const POLICES = ['Calibri', 'Arial', 'Times New Roman', 'Verdana', 'Georgia', 'Tahoma', 'Cambria'];
const NIVEAUX = [
  { k: 'h1' as const, label: 'Titre du document (H1)' },
  { k: 'h2' as const, label: 'Titre de section (H2)' },
  { k: 'h3' as const, label: 'Sous-titre (H3)' },
];

function OfficeParams({ value, onChange }: { value: OfficeConf; onChange: (v: OfficeConf) => void }) {
  const [nouveau, setNouveau] = useState('');
  const setTitre = (k: 'h1' | 'h2' | 'h3', patch: Partial<OfficeConf['titres']['h1']>) =>
    onChange({ ...value, titres: { ...value.titres, [k]: { ...value.titres[k], ...patch } } });
  const setTrame = (trame: string[]) => onChange({ ...value, trame });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 18 }}>  {/* `minmax(0, 1fr)` : un élément de grille refuse par défaut de
      descendre sous sa largeur de contenu, si bien qu'un registre large
      étirait le bandeau de titre et la barre de filtres hors du cadre
      (constaté sur les NC le 19/08/2026). Le `0` autorise la colonne à
      se réduire ; ce qui est large défile dans son propre conteneur. */}
      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
        <div>
          <label style={lbl}>Police du corps de texte</label>
          <select value={value.police} onChange={(e) => onChange({ ...value, police: e.target.value })} style={inputStyle}>
            {POLICES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Taille du corps (pt)</label>
          <input type="number" min={8} max={16} value={value.taille}
            onChange={(e) => onChange({ ...value, taille: +e.target.value || 11 })} style={inputStyle} />
        </div>
      </div>

      <div>
        <div style={{ ...lbl, marginBottom: 8 }}>Styles de titres</div>
        <div style={{ display: 'grid', gap: 8 }}>
          {NIVEAUX.map(({ k, label }) => {
            const t = value.titres[k];
            return (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '9px 12px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)' }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)', flex: '1 1 150px' }}>{label}</span>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-muted)' }}>
                  Taille
                  <input type="number" min={9} max={28} value={t.taille}
                    onChange={(e) => setTitre(k, { taille: +e.target.value || t.taille })}
                    style={{ ...inputStyle, width: 62, height: 30, fontSize: 12.5 }} />
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-muted)' }}>
                  Couleur
                  <input type="color" value={t.couleur} onChange={(e) => setTitre(k, { couleur: e.target.value })}
                    style={{ width: 34, height: 30, padding: 2, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-card)', cursor: 'pointer' }} />
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-body)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={t.gras} onChange={(e) => setTitre(k, { gras: e.target.checked })} style={{ accentColor: 'var(--color-primary)' }} />
                  Gras
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-body)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={t.majuscules} onChange={(e) => setTitre(k, { majuscules: e.target.checked })} style={{ accentColor: 'var(--color-primary)' }} />
                  Majuscules
                </label>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)', cursor: 'pointer', marginBottom: 8 }}>
          <input type="checkbox" checked={value.trameActive} onChange={(e) => onChange({ ...value, trameActive: e.target.checked })} style={{ accentColor: 'var(--color-primary)' }} />
          Trame par défaut des nouveaux documents
        </label>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 10px', lineHeight: 1.5 }}>
          Sections posées automatiquement dans un document Word créé sans contenu déjà rédigé. Un document
          qui a déjà du contenu reprend ce contenu — la trame ne s’y substitue jamais.
        </p>
        <div style={{ display: 'grid', gap: 6, opacity: value.trameActive ? 1 : 0.5 }}>
          {value.trame.map((s, i) => (
            <div key={`${s}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)', width: 20 }}>{i + 1}.</span>
              <input value={s} disabled={!value.trameActive}
                onChange={(e) => setTrame(value.trame.map((x, j) => (j === i ? e.target.value : x)))}
                style={{ ...inputStyle, height: 32, fontSize: 12.5, flex: 1 }} />
              <Bouton taille="sm" disabled={!value.trameActive || i === 0} title="Monter" onClick={() => { const t = [...value.trame]; [t[i - 1], t[i]] = [t[i], t[i - 1]]; setTrame(t); }}>↑</Bouton>
              <Bouton taille="sm" disabled={!value.trameActive || i === value.trame.length - 1} title="Descendre" onClick={() => { const t = [...value.trame]; [t[i + 1], t[i]] = [t[i], t[i + 1]]; setTrame(t); }}>↓</Bouton>
              <Bouton taille="sm" disabled={!value.trameActive} title="Retirer la section" onClick={() => setTrame(value.trame.filter((_, j) => j !== i))}><I n="trash" s={12} /></Bouton>
            </div>
          ))}
          <span style={{ display: 'inline-flex', gap: 6, marginTop: 2 }}>
            <input value={nouveau} disabled={!value.trameActive} onChange={(e) => setNouveau(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && nouveau.trim()) { setTrame([...value.trame, nouveau.trim()]); setNouveau(''); } }}
              placeholder="Nouvelle section…" style={{ ...inputStyle, height: 34, width: 220, fontSize: 12.5 }} />
            <Bouton variante="primaire" taille="sm" disabled={!value.trameActive} onClick={() => { if (nouveau.trim()) { setTrame([...value.trame, nouveau.trim()]); setNouveau(''); } }}>Ajouter</Bouton>
          </span>
        </div>
      </div>

      {/* Aperçu : reproduit la feuille de style envoyée au convertisseur. */}
      <div>
        <div style={{ ...lbl, marginBottom: 8 }}>Aperçu</div>
        <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '18px 20px', background: 'var(--surface-card)', fontFamily: `'${value.police}', sans-serif` }}>
          <div style={{ fontSize: value.titres.h1.taille, color: value.titres.h1.couleur, fontWeight: value.titres.h1.gras ? 700 : 400, textTransform: value.titres.h1.majuscules ? 'uppercase' : 'none', marginBottom: 8 }}>
            Titre du document
          </div>
          <div style={{ fontSize: value.titres.h2.taille, color: value.titres.h2.couleur, fontWeight: value.titres.h2.gras ? 700 : 400, textTransform: value.titres.h2.majuscules ? 'uppercase' : 'none', marginBottom: 4 }}>
            {value.trameActive && value.trame[0] ? `1. ${value.trame[0]}` : 'Titre de section'}
          </div>
          <div style={{ fontSize: value.taille, color: 'var(--text-strong)', marginBottom: 8 }}>
            Corps de texte en {value.police} {value.taille} pt — l’aperçu reflète la mise en forme appliquée au fichier Word.
          </div>
          <div style={{ fontSize: value.titres.h3.taille, color: value.titres.h3.couleur, fontWeight: value.titres.h3.gras ? 700 : 400, textTransform: value.titres.h3.majuscules ? 'uppercase' : 'none' }}>
            Sous-titre
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Codification des références (schéma composable · segments ordonnés) ----
/** Les implantations physiques du laboratoire.
 *
 *  Cette liste existait déjà dans `ged-setting.sites` — elle alimentait le
 *  sélecteur de l'assistant de demande et l'abréviation portée dans la
 *  référence — mais elle n'était modifiable NULLE PART : sa seule origine
 *  était le seed (`['Monastir — Ruspina']`). Ouvrir un second site imposait
 *  donc de passer en base. C'est ce que ce panneau corrige.
 *
 *  Un site ne se RENOMME pas ici, et c'est délibéré : `ged-document.site` est
 *  une chaîne, pas une relation. Renommer laisserait les documents déjà créés
 *  porter l'ancien libellé, sans que rien ne le signale — exactement la
 *  famille de panne que le dépôt recense. Retirer un site est en revanche
 *  sans danger : les documents gardent leur valeur, seule la LISTE PROPOSÉE
 *  à la création se réduit. Le panneau le dit en toutes lettres. */
function SitesPanel({ draft, onSet }: { draft: GedSettings; onSet: (patch: Partial<GedSettings>) => void }) {
  const [v, setV] = useState('');
  const sites = draft.sites || [];
  const doublon = sites.find((s) => aplati(s) === aplati(v));
  const ajoutable = v.trim().length > 0 && !doublon;

  const ajouter = () => {
    if (!ajoutable) return;
    const nom = v.trim();
    // Premier site déclaré = site principal, sinon on ne touche à rien.
    onSet({ sites: [...sites, nom], ...(sites.length ? {} : { activeSite: nom }) });
    setV('');
  };

  const retirer = (nom: string) => {
    const restants = sites.filter((s) => s !== nom);
    onSet({
      sites: restants,
      // Le site principal doit rester un site déclaré : sans ce report, la
      // référence d'un nouveau document irait chercher un code de site qui
      // n'existe plus, et retomberait en silence sur l'abréviation dérivée.
      ...(draft.activeSite === nom ? { activeSite: restants[0] || '' } : {}),
    });
  };

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ display: 'grid', gap: 6 }}>
        {sites.length === 0 && (
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
            Aucun site déclaré. Tant que la liste est vide, le champ « Site » d’un nouveau document reste vide.
          </p>
        )}
        {sites.map((s) => {
          const principal = draft.activeSite === s;
          return (
            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '9px 12px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)' }}>
              <span style={{ flex: '1 1 180px', fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>{s}</span>
              {principal ? (
                <span style={{ background: 'var(--cyan-50)', color: 'var(--cyan-700)', borderRadius: 'var(--radius-pill)', fontSize: 10.5, fontWeight: 700, padding: '3px 9px' }}>
                  site principal
                </span>
              ) : (
                <Bouton taille="sm" onClick={() => onSet({ activeSite: s })}>
                  Définir principal
                </Bouton>
              )}
              <Bouton taille="sm" onClick={() => retirer(s)} aria-label={`Retirer le site ${s}`} title="Retirer de la liste proposée">
                <I n="trash" s={12} />
              </Bouton>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <input
          value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ajouter(); } }}
          placeholder="Nouveau site…"
          aria-label="Nom du nouveau site"
          aria-invalid={doublon ? true : undefined}
          style={{ ...inputStyle, width: 240, ...(doublon ? { borderColor: 'var(--amber-600)' } : {}) }}
        />
        <Bouton onClick={ajouter} disabled={!ajoutable}>
          Ajouter
        </Bouton>
        {doublon && (
          <span style={{ fontSize: 12.5, color: 'var(--amber-700)', alignSelf: 'center', lineHeight: 1.45 }}>
            <strong>{doublon}</strong> est déjà déclaré.
          </span>
        )}
      </div>

      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.55, borderTop: '1px solid var(--border-default)', paddingTop: 12 }}>
        Retirer un site ne modifie <strong>aucun document déjà créé</strong> : il garde le site inscrit à sa création,
        qui reste lisible sur sa fiche et au journal. Seule la liste proposée à la création se réduit.
        Pour la même raison, un site ne se renomme pas ici — déclarez le nouveau nom et retirez l’ancien.
      </p>
    </div>
  );
}

function CodificationPanel({ draft, onSet }: { draft: GedSettings; onSet: (patch: Partial<GedSettings>) => void }) {
  const codif = { ...CODIF_DEFAULT, ...(draft.codification || {}) };
  const segments: CodifSegment[] = codif.segments?.length ? codif.segments : DEFAULT_SEGMENTS;
  const setCodif = (patch: Partial<GedSettings['codification']>) =>
    onSet({ codification: { ...codif, ...patch } as GedSettings['codification'] });

  // Registres : mêmes défauts que le serveur (`CODIF_REGISTRES_DEFAUT`), pour
  // que l'aperçu affiche EXACTEMENT ce que `genRef` attribuera.
  const reg = { ...CODIF_REGISTRES_DEFAUT, codes: {} as Record<string, string>, ...(codif.registres || {}) };
  const setReg = (patch: Partial<NonNullable<NonNullable<GedSettings['codification']>['registres']>>) =>
    setCodif({ registres: { ...reg, ...patch } });

  const toggleSeg = (k: CodifSegment) =>
    setCodif({ segments: segments.includes(k) ? segments.filter((s) => s !== k) : [...segments, k] });
  const moveSeg = (i: number, dir: -1 | 1) => {
    const next = [...segments];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setCodif({ segments: next });
  };

  const setDiscCode = (i: number, code: string) =>
    onSet({ disciplines: draft.disciplines.map((d, j) => (j === i ? { ...d, code: code.toUpperCase() } : d)) });
  const setProcCodif = (id: string, code: string) =>
    onSet({ processus: (draft.processus || []).map((p) => (p.id === id ? { ...p, codif: code.toUpperCase() } : p)) });
  const setSiteCode = (site: string, code: string) =>
    setCodif({ siteCodes: { ...(codif.siteCodes || {}), [site]: code.toUpperCase() } });

  // Sources actives, dans l'ordre de l'écran correspondant.
  const procsActifs = (draft.processus || []).filter((p) => p.active);
  const discsActives = draft.disciplines.filter((d) => d.active);
  const sites = draft.sites || [];

  // Aperçu vivant : première valeur active de chaque source.
  const type0 = (draft.docTypes.find((t) => t.active) || draft.docTypes[0])?.code || 'MO';
  const disc0 = discsActives[0] || draft.disciplines[0];
  const proc0 = procsActifs[0];
  const site0 = sites[0];
  const apercu = genCodePreview(type0, disc0?.name || '', 1, {
    separator: codif.separator, seqPad: codif.seqPad, prefix: codif.prefix, segments,
    discCode: disc0?.code, processCode: proc0 ? procCodif(proc0) : '',
    siteCode: site0 ? (codif.siteCodes?.[site0] || autoDiscCode(site0).slice(0, 3)) : '',
  });
  const legende = [...segments.map((s) => CODIF_SEGMENTS.find((x) => x.key === s)?.label || s), 'n°'].join(' · ');

  const boxRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)' };
  const codeInput: React.CSSProperties = { ...inputStyle, height: 32, width: 130, fontFamily: 'var(--font-mono)', fontSize: 12.5, textAlign: 'center' };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 18 }}>  {/* `minmax(0, 1fr)` : un élément de grille refuse par défaut de
      descendre sous sa largeur de contenu, si bien qu'un registre large
      étirait le bandeau de titre et la barre de filtres hors du cadre
      (constaté sur les NC le 19/08/2026). Le `0` autorise la colonne à
      se réduire ; ce qui est large défile dans son propre conteneur. */}
      {/* Aperçu ÉPINGLÉ en haut de la zone défilante (comme l'aperçu de fiche du
          manuel) : il reste visible pendant qu'on règle segments et codes. */}
      <div style={{ position: 'sticky', top: -22, zIndex: 5, margin: '-22px -26px 0', padding: '14px 26px', background: 'var(--surface-card)', borderBottom: '1px solid var(--border-default)' }}>
        <div style={{ ...lbl, marginBottom: 8 }}>Aperçu de la référence</div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '10px 16px', background: 'var(--surface-sunken)' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 16, fontWeight: 700, color: 'var(--navy-900)' }}>{apercu}</span>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{legende}</span>
        </div>
      </div>

      {/* Composition : quels segments, dans quel ordre. Le n° séquentiel est
          toujours en dernier (non déplaçable) — le calcul du prochain numéro
          s'appuie sur cet invariant. */}
      <div>
        <div style={{ ...lbl, marginBottom: 8 }}>1 · Composition de la référence des DOCUMENTS</div>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 10px', lineHeight: 1.5 }}>
          Procédures, modes opératoires, enregistrements, formulaires — tout ce qui porte un <strong>type</strong> et
          une <strong>discipline</strong>. Cochez les éléments à inclure et ordonnez-les ; le numéro séquentiel
          clôt toujours la référence, et c’est lui qui garantit l’unicité.
        </p>
        <div style={{ display: 'grid', gap: 6 }}>
          {CODIF_SEGMENTS.map((seg) => {
            const on = segments.includes(seg.key);
            const pos = segments.indexOf(seg.key);
            return (
              <div key={seg.key} style={{ ...boxRow, opacity: on ? 1 : 0.7 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flex: 1, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>
                  <input type="checkbox" checked={on} onChange={() => toggleSeg(seg.key)} style={{ accentColor: 'var(--color-primary)' }} />
                  {seg.label}
                  {on && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>#{pos + 1}</span>}
                </label>
                {on && (
                  <span style={{ display: 'inline-flex', gap: 4 }}>
                    <Bouton taille="sm" onClick={() => moveSeg(pos, -1)} disabled={pos === 0} title="Monter">↑</Bouton>
                    <Bouton taille="sm" onClick={() => moveSeg(pos, 1)} disabled={pos === segments.length - 1} title="Descendre">↓</Bouton>
                  </span>
                )}
              </div>
            );
          })}
          <div style={{ ...boxRow, opacity: 0.75 }}>
            <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)' }}>Numéro séquentiel <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>(toujours en dernier)</span></span>
          </div>
        </div>
      </div>

      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px 16px' }}>
        {segments.includes('prefix') && (
          <div>
            <label style={lbl}>Pré-référence (valeur)</label>
            <input value={codif.prefix || ''} maxLength={12} onChange={(e) => setCodif({ prefix: e.target.value })}
              placeholder="ex. BIO24" style={{ ...inputStyle, fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }} />
            <div style={{ fontSize: 12.5, color: 'var(--text-faint)', marginTop: 5, lineHeight: 1.4 }}>Préfixe fixe (multi-sites / multi-entités).</div>
          </div>
        )}
        <div>
          <label style={lbl}>Séparateur</label>
          <input value={codif.separator} maxLength={2} onChange={(e) => setCodif({ separator: e.target.value })}
            placeholder="-" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
        </div>
        <div>
          <label style={lbl}>Longueur du numéro (chiffres)</label>
          <input type="number" min={1} max={6} value={codif.seqPad}
            onChange={(e) => setCodif({ seqPad: Math.min(6, Math.max(1, +e.target.value || 3)) })}
            style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
        </div>
      </div>

      {segments.includes('process') && (
        <div>
          <div style={{ ...lbl, marginBottom: 8 }}>Abréviation par processus</div>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 10px', lineHeight: 1.5 }}>
            Code court (ex. PRE, MET) porté dans la référence. Seuls les processus <strong>actifs</strong> (écran Processus) sont codifiables ; désactivez-en un là-bas pour l’exclure.
          </p>
          <div style={{ display: 'grid', gap: 6 }}>
            {procsActifs.length === 0 && <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>Aucun processus actif. Ajoutez-en dans l’écran Processus.</p>}
            {procsActifs.map((p) => (
              <div key={p.id} style={boxRow}>
                {p.codif && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--text-on-primary)', background: 'var(--navy-900)', borderRadius: 'var(--radius-sm)', padding: '2px 7px' }}>{p.codif}</span>}
                <span style={{ flex: '1 1 160px', fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>{p.name}</span>
                {/* Le code dérivé s'affichait en PLACEHOLDER, donc en gris DANS le
                    champ : indiscernable d'une valeur déjà saisie. On croyait le
                    code attribué et on n'y touchait pas. Il est désormais hors du
                    champ et nommé « auto », ce qui dit à la fois qu'il s'applique
                    et qu'il n'a pas été choisi. */}
                {!(p.codif || '').trim() && (
                  <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-faint)' }}>
                    auto : {procCodif(p)}
                  </span>
                )}
                <input value={p.codif || ''} maxLength={5} aria-label={`Code de codification — ${p.name}`}
                  onChange={(e) => setProcCodif(p.id, e.target.value)} style={codeInput} />
              </div>
            ))}
          </div>
        </div>
      )}

      {segments.includes('discipline') && (
        <div>
          <div style={{ ...lbl, marginBottom: 8 }}>Code par discipline</div>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 10px', lineHeight: 1.5 }}>
            Abréviation portée dans la référence. Laissée vide, elle est dérivée du nom.
          </p>
          <div style={{ display: 'grid', gap: 6 }}>
            {draft.disciplines.map((d, i) => (
              <div key={d.name} style={boxRow}>
                <span style={{ flex: '1 1 160px', fontSize: 12.5, fontWeight: 600, color: d.active ? 'var(--text-strong)' : 'var(--text-muted)' }}>{d.name}{!d.active && <span style={{ fontSize: 12.5, color: 'var(--text-faint)' }}> · inactive</span>}</span>
                {!(d.code || '').trim() && (
                  <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-faint)' }}>
                    auto : {autoDiscCode(d.name)}
                  </span>
                )}
                <input value={d.code || ''} maxLength={8} aria-label={`Code de codification — ${d.name}`}
                  onChange={(e) => setDiscCode(i, e.target.value)} style={codeInput} />

                {/* ── LA SUBDIVISION (25/08/2026) ──────────────────────────────
                    Être habilité au parent vaut pour ses filles. Sans cette
                    règle, créer « Virologie » invaliderait d'un coup les
                    habilitations déjà prononcées en « Microbiologie ». */}
                <select value={d.parent || ''} aria-label={`Subdivision de — ${d.name}`}
                  onChange={(e) => onSet({ disciplines: draft.disciplines.map((x, j) => (j === i ? { ...x, parent: e.target.value || null } : x)) })}
                  style={{ ...codeInput, width: 150, fontFamily: 'var(--font-sans)' }}>
                  <option value="">— discipline principale</option>
                  {draft.disciplines
                    .filter((x, j) => j !== i && (x.code || '').trim() && x.parent !== d.code)
                    .map((x) => <option key={x.code} value={x.code}>subdivision de {x.name}</option>)}
                </select>

                {/* Les libellés du CATALOGUE que cette discipline couvre. Sans
                    eux, le rattachement retombe sur la comparaison de noms —
                    qu'un renommage rompt en silence. */}
                <input value={(d.familles || []).join(', ')} placeholder="familles couvertes (vide = par nom)"
                  aria-label={`Familles du catalogue couvertes — ${d.name}`}
                  onChange={(e) => onSet({
                    disciplines: draft.disciplines.map((x, j) => (j === i
                      ? { ...x, familles: e.target.value.split(',').map((v) => v.trim()).filter(Boolean) }
                      : x)),
                  })}
                  style={{ ...codeInput, width: 230, fontFamily: 'var(--font-sans)' }} />
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.55 }}>
            <strong>Familles couvertes</strong> : les libellés tels qu’ils figurent sur les fiches du
            catalogue. Laissés vides, le rattachement se fait par comparaison de noms — ce qui marche
            tant que personne ne renomme la discipline. Une <strong>sous-famille</strong> peut y
            figurer (« Virologie ») : la déclaration la plus fine l’emporte.
            <br />
            <strong>Subdivision de</strong> : la discipline dont celle-ci est une branche. Être
            habilité à la principale vaut pour ses subdivisions ; l’inverse est faux — une compétence
            fine ne remonte pas.
          </p>
        </div>
      )}

      {segments.includes('site') && (
        <div>
          <div style={{ ...lbl, marginBottom: 8 }}>Code par site</div>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 10px', lineHeight: 1.5 }}>
            Abréviation du site portée dans la référence (labos multi-sites). Laissée vide, elle est dérivée du nom.
          </p>
          <div style={{ display: 'grid', gap: 6 }}>
            {sites.length === 0 && <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>Un seul site déclaré.</p>}
            {sites.map((s) => (
              <div key={s} style={boxRow}>
                <span style={{ flex: '1 1 160px', fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>{s}</span>
                <input value={codif.siteCodes?.[s] || ''} placeholder={autoDiscCode(s).slice(0, 3)} maxLength={5}
                  onChange={(e) => setSiteCode(s, e.target.value)} style={codeInput} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* La RÈGLE D'UNICITÉ, écrite là où l'on règle — pas dans une note de bas
          de page. C'est elle que la RQ a demandé le 18/08 : « il faut que la
          règle soit que cela ne puisse jamais identifier deux éléments avec la
          même référence ». */}
      <div style={{ border: '1px solid var(--cyan-200)', background: 'var(--cyan-50)', borderRadius: 'var(--radius-md)', padding: '12px 14px' }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)', marginBottom: 6 }}>
          Deux schémas, une seule règle
        </div>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.55 }}>
          Les <strong>documents</strong> se réfèrent par type et discipline ; les <strong>registres</strong> par
          nature de fiche. Deux besoins différents, donc deux schémas — mais <strong>une seule règle</strong> :
          une référence ne désigne jamais deux éléments. Elle est tenue par le numéro séquentiel, compté
          <em> par registre et par année</em>, et jamais réattribué : un dossier supprimé ne rend pas son numéro.
        </p>
      </div>

      {/* ---- Registres -------------------------------------------------------
          Les non-conformités, actions, réclamations, risques et calibrations ne
          portent ni type ni discipline : leur référence suit un schéma à part,
          jusqu'ici écrit en dur dans le moteur. Réglé ici, avec le reste.
          La liste vient de `REGISTRES_CODIFIES` et doit rester le miroir exact
          des tags passés à `genRef` côté serveur : tout ce que le réglage
          renomme doit être annoncé ici (les calibrations manquaient). */}
      <div style={{ borderTop: '1px solid var(--border-default)', paddingTop: 18 }}>
        <div style={{ ...lbl, marginBottom: 8 }}>2 · Références des registres</div>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
          Non-conformités, actions, réclamations, risques, calibrations, objectifs, incertitudes, EEQ…
          Ces fiches <strong>ne portent ni type ni discipline</strong> : leur référence ne peut donc pas suivre
          le schéma des documents, et c’est la seule raison pour laquelle il existe deux systèmes.
          <br />
          <strong>Les références déjà attribuées ne changent jamais</strong> — elles sont inscrites au journal
          signé, dans les liens entre dossiers et dans les adresses partagées. Modifier le schéma ouvre une
          <strong> nouvelle série</strong> : l’ancienne garde ses numéros, la nouvelle repart de 1.
          Chaque compteur est propre à son registre et à son année.
        </p>
        <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: '12px 16px', marginBottom: 14 }}>
          <div>
            <label style={lbl} htmlFor="reg-prefix">Préfixe</label>
            <input id="reg-prefix" value={reg.prefix} placeholder={CODIF_REGISTRES_DEFAUT.prefix} maxLength={12}
              onChange={(e) => setReg({ prefix: e.target.value.toUpperCase() })} style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
          </div>
          <div>
            <label style={lbl} htmlFor="reg-sep">Séparateur</label>
            <input id="reg-sep" value={reg.separator} maxLength={2}
              onChange={(e) => setReg({ separator: e.target.value })} style={{ ...inputStyle, fontFamily: 'var(--font-mono)', textAlign: 'center' }} />
          </div>
          <div>
            <label style={lbl} htmlFor="reg-pad">Chiffres du n°</label>
            <input id="reg-pad" type="number" min={1} max={6} value={reg.seqPad}
              onChange={(e) => setReg({ seqPad: Math.max(1, Math.min(6, +e.target.value || 3)) })} style={inputStyle} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 22, cursor: 'pointer' }}>
            <input type="checkbox" checked={reg.annee}
              onChange={(e) => setReg({ annee: e.target.checked })}
              style={{ accentColor: 'var(--color-primary)', width: 16, height: 16 }} />
            <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>Inclure l’année</span>
          </label>
        </div>
        <div style={{ display: 'grid', gap: 6 }}>
          {REGISTRES_CODIFIES.map((r) => (
            <div key={r.tag} style={boxRow}>
              <span style={{ flex: '1 1 160px', fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>{r.label}</span>
              {/* Le compteur est montré TEL QU'IL SERA EN ROUTINE — la
                  quarante-deuxième fiche de l'année, pas la première. Un aperçu
                  figé à « 001 » cache précisément ce qu'on vient régler : la
                  longueur du numéro. Avec trois chiffres « 042 », avec deux
                  « 42 » — et c'est cette différence qu'on veut voir. */}
              <span style={{ fontSize: 12.5, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}
                title={`Exemple : la 42ᵉ fiche de ce registre cette année. Première : ${apercuRefRegistre(r.tag, { ...reg, code: reg.codes?.[r.tag] }, 1)}`}>
                {apercuRefRegistre(r.tag, { ...reg, code: reg.codes?.[r.tag] }, 42)}
              </span>
              <input value={reg.codes?.[r.tag] || ''} placeholder={r.tag} maxLength={6}
                aria-label={`Code de registre — ${r.label}`}
                onChange={(e) => setReg({ codes: { ...(reg.codes || {}), [r.tag]: e.target.value.toUpperCase() } })}
                style={codeInput} />
            </div>
          ))}
        </div>
      </div>

      {/* ---- Organigrammes ---------------------------------------------------
          Ce ne sont pas des références générées mais celles des DOCUMENTS papier
          du laboratoire. Elles étaient écrites en dur dans les onglets de
          l'écran Organigramme. */}
      <div style={{ borderTop: '1px solid var(--border-default)', paddingTop: 18 }}>
        <div style={{ ...lbl, marginBottom: 8 }}>Références des organigrammes</div>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
          Affichées sur l’écran Organigramme. Ce sont les références de vos <strong>documents</strong>,
          pas des numéros attribués automatiquement : à vous de les faire correspondre à votre système documentaire.
        </p>
        <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
          <div>
            <label style={lbl} htmlFor="org-ref-f">Organigramme fonctionnel</label>
            <input id="org-ref-f" value={codif.orgRefs?.fonctionnel ?? ''} placeholder={ORG_REFS_DEFAUT.fonctionnel}
              onChange={(e) => setCodif({ orgRefs: { ...(codif.orgRefs || {}), fonctionnel: e.target.value } })}
              style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
          </div>
          <div>
            <label style={lbl} htmlFor="org-ref-n">Organigramme nominatif</label>
            <input id="org-ref-n" value={codif.orgRefs?.nominatif ?? ''} placeholder={ORG_REFS_DEFAUT.nominatif}
              onChange={(e) => setCodif({ orgRefs: { ...(codif.orgRefs || {}), nominatif: e.target.value } })}
              style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Comparaison de libellés indifférente à la casse et aux accents : « Pré-analytique »
 *  et « pre analytique » désignent la même chose, et c'est bien la MÊME erreur. */
const aplati = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z]/g, '');

/* ═══════════════════════════════════════════════════════════════════════════
 * LES DISCIPLINES EN DIAGRAMME — demande de la RQ du 07/09/2026
 *
 * « Laisse-moi uniquement les disciplines et je veux que tu les affiches en
 * format diagramme. Notamment quand il y a une sous-famille. On peut rajouter
 * des sous-familles avec des plus comme dans un diagramme. »
 *
 * ── Ce que le diagramme RÉVÈLE, et qu'il n'invente pas ─────────────────────
 *
 * Le rattachement existait DÉJÀ en base : une discipline porte un `parent`,
 * c'est-à-dire le code de celle dont elle est une subdivision. Une liste plate
 * de pastilles le rendait invisible — on voyait vingt familles côte à côte
 * alors que certaines sont les sous-familles d'autres. Le diagramme ne modélise
 * rien de neuf : il montre ce qui était déjà écrit.
 *
 * ⚠️ La règle d'héritage compte, et le diagramme la rend enfin lisible : être
 * habilité au PARENT vaut pour ses filles, l'inverse est faux.
 *
 * ── Les deux cartes retirées ──────────────────────────────────────────────
 *
 * « Ce n'est pas un processus » et « Famille & sous-famille » encombraient cet
 * onglet. La première disait ce que le champ d'ajout refuse déjà, nommément et
 * au bon moment — la garde reste, la leçon partait avec elle. La seconde
 * renvoyait vers un écran par un bouton qui, de l'aveu de la RQ, « ne mène
 * nulle part » ; et son propos — famille et discipline sont deux listes
 * séparées — cesse d'être vrai le jour où l'on décide que la famille du
 * catalogue EST la discipline. C'est ce que dit maintenant l'intitulé.
 * ═══════════════════════════════════════════════════════════════════════════ */
type Disc = { name: string; active: boolean; code?: string; parent?: string | null; familles?: string[] };

function ArbreDisciplines({ disciplines, processus, onChange }: {
  disciplines: Disc[]; processus: string[]; onChange: (d: Disc[]) => void;
}) {
  /* La clé d'un nœud : son `code` quand il en a un, son nom sinon. Les fiches
     anciennes n'ont pas toutes un code, et les exclure du diagramme les rendrait
     invisibles — donc immodifiables. */
  const cle = (d: Disc) => d.code || d.name;
  const enfantsDe = (parent: string | null) => disciplines.filter(
    (d) => (d.parent || null) === parent,
  );
  /* ⚠️ Un `parent` qui ne désigne aucune discipline existante ferait DISPARAÎTRE
     la fille du diagramme : elle n'est ni racine, ni enfant de quoi que ce soit.
     On les rattache donc à la racine, plutôt que de les perdre en silence. */
  const cles = new Set(disciplines.map(cle));
  const racines = disciplines.filter((d) => !d.parent || !cles.has(d.parent));

  const [ajoutSous, setAjoutSous] = React.useState<string | null>(null);

  const basculer = (d: Disc) => onChange(disciplines.map(
    (x) => (cle(x) === cle(d) ? { ...x, active: !x.active } : x),
  ));
  const ajouter = (nom: string, parent: string | null) => onChange(
    [...disciplines, { name: nom, active: true, parent }],
  );

  const Noeud = ({ d, profondeur }: { d: Disc; profondeur: number }) => {
    const enfants = enfantsDe(cle(d));
    return (
      <li style={{ position: 'relative', listStyle: 'none', paddingInlineStart: profondeur ? 22 : 0 }}>
        {/* Les traits du diagramme : un coude vers le nœud, un trait vertical
            qui court jusqu'au dernier frère. Dessinés en bordures plutôt qu'en
            SVG — ils suivent alors la hauteur réelle du texte, qui s'enroule. */}
        {profondeur > 0 && (
          <span aria-hidden style={{
            position: 'absolute', insetInlineStart: 6, top: 0, width: 11, height: 17,
            borderInlineStart: '1px solid var(--border-default)',
            borderBottom: '1px solid var(--border-default)',
            borderEndStartRadius: 5,
          }} />
        )}
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 6,
          background: d.active ? 'var(--surface-card)' : 'var(--gray-25)', border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-pill)', padding: '5px 7px 5px 13px', fontSize: 12.5, fontWeight: 600,
          color: d.active ? 'var(--text-strong)' : 'var(--text-muted)',
        }}>
          {d.name}
          <button onClick={() => basculer(d)} title={d.active ? 'Désactiver' : 'Activer'}
            style={{ border: 'none', background: d.active ? 'var(--cyan-50)' : 'var(--gray-50)', color: d.active ? 'var(--cyan-700)' : 'var(--text-muted)', borderRadius: 'var(--radius-pill)', fontSize: 10.5, fontWeight: 700, padding: '3px 8px', cursor: 'pointer' }}>
            {d.active ? 'active' : 'inactive'}
          </button>
          <button onClick={() => setAjoutSous(ajoutSous === cle(d) ? null : cle(d))}
            aria-expanded={ajoutSous === cle(d)}
            title={`Ajouter une sous-famille sous « ${d.name} »`}
            aria-label={`Ajouter une sous-famille sous ${d.name}`}
            style={{ border: 'none', background: ajoutSous === cle(d) ? 'var(--cyan-600)' : 'var(--gray-50)', color: ajoutSous === cle(d) ? 'var(--surface-card)' : 'var(--text-muted)', borderRadius: '50%', width: 20, height: 20, lineHeight: 1, fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            +
          </button>
        </span>

        {ajoutSous === cle(d) && (
          <div style={{ paddingInlineStart: 22, marginBottom: 8 }}>
            <AddDisc processus={processus} sousFamilleDe={d.name}
              onAdd={(nom) => { ajouter(nom, cle(d)); setAjoutSous(null); }} />
          </div>
        )}

        {enfants.length > 0 && (
          <ul style={{ margin: 0, padding: 0 }}>
            {enfants.map((e) => <Noeud key={cle(e)} d={e} profondeur={profondeur + 1} />)}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div>
      <ul style={{ margin: 0, padding: 0 }}>
        {racines.map((d) => <Noeud key={cle(d)} d={d} profondeur={0} />)}
      </ul>
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--border-default)' }}>
        <AddDisc processus={processus} onAdd={(nom) => ajouter(nom, null)} />
      </div>
    </div>
  );
}

function AddDisc({ onAdd, processus, sousFamilleDe }: {
  onAdd: (name: string) => void; processus: string[];
  /** Nommé quand l'ajout se fait SOUS un nœud : le champ doit le dire, sans quoi
   *  deux champs identiques à deux endroits de l'écran font ajouter à la racine
   *  ce qu'on voulait ranger sous une famille. */
  sousFamilleDe?: string;
}) {
  const [v, setV] = useState('');
  // Un processus saisi ici est REFUSÉ, avec son nom et le chemin pour le
  // corriger. Un champ qui accepte tout et laisse le désordre s'installer coûte
  // plus cher qu'un refus expliqué : c'est ainsi que « Pré-analytique » avait
  // fini par classer 26 documents sur une famille biologique qui n'existe pas.
  const collision = processus.find((p) => aplati(p) === aplati(v));
  const doublon = !collision && v.trim().length > 0;
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexDirection: 'column' }}>
      <span style={{ display: 'inline-flex', gap: 6 }}>
        <input
          value={v} onChange={(e) => setV(e.target.value)}
          placeholder={sousFamilleDe ? `Sous-famille de « ${sousFamilleDe} »…` : 'Nouvelle famille biologique…'}
          aria-invalid={collision ? true : undefined}
          style={{ ...inputStyle, width: 200, ...(collision ? { borderColor: 'var(--amber-600)' } : {}) }}
        />
        <Bouton
          onClick={() => { if (doublon) { onAdd(v.trim()); setV(''); } }}
          disabled={!!collision || !v.trim()}
          title={collision ? `« ${collision} » est un processus, pas une discipline` : undefined}
        >Ajouter</Bouton>
      </span>
      {collision && (
        <span style={{ fontSize: 12.5, color: 'var(--amber-700)', maxWidth: 300, lineHeight: 1.45 }}>
          <strong>{collision}</strong> est un <strong>processus</strong>, pas une famille biologique — il traverse
          toutes les disciplines. Il se règle dans le module Processus.
        </span>
      )}
    </span>
  );
}

/** Rappel de la frontière, avec la liste réelle des processus en regard.
 *
 *  Dire « ne mettez pas de processus ici » sans montrer lesquels reviendrait à
 *  demander de deviner. La liste est en LECTURE SEULE : les processus portent
 *  un pilote et un suppléant, ils se modifient là où cette responsabilité est
 *  visible, pas dans un coin du paramétrage. */
// ---- Validité des matériaux après ouverture / reconstitution ----
//
// La durée est portée par le PRODUIT, pas par le flacon : c'est une propriété
// du réactif, la même d'un flacon à l'autre. Sur les 188 flacons enregistrés il
// n'y a que 31 produits — la saisir à la réception, ce serait 188 saisies et
// autant d'occasions de diverger sur un chiffre censé être unique.
//
// Faute de référentiel de produits en base, la liste proposée vient des données
// réelles : `GET ged/materiaux` rend les produits distincts rencontrés dans les
// deux registres, AVEC leur clé. Cette clé n'est pas reconstruite ici — une
// normalisation qui différerait d'un caractère ferait enregistrer la durée sous
// une clé que le serveur ne relit jamais : un réglage saisi, sauvegardé, et
// sans le moindre effet.
type ProduitMateriau = { cle: string; famille: string; libelle: string; flacons: number; validiteJours: number | null };
type ValiditeConf = NonNullable<GedSettings['materiauValidites']>;

function ValiditeMateriaux({ value, onChange }: { value?: ValiditeConf; onChange: (v: ValiditeConf) => void }) {
  const conf: ValiditeConf = value ?? {};
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div>
        <label style={lbl} htmlFor="mat-seuil">Prévenir combien de jours avant l’échéance</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input id="mat-seuil" type="number" min={1} inputMode="numeric"
            value={conf.seuilApprocheJours ? String(conf.seuilApprocheJours) : ''} placeholder="60"
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              onChange({ ...conf, seuilApprocheJours: Number.isFinite(n) && n > 0 ? n : undefined });
            }}
            style={{ ...inputStyle, height: 34, width: 90, fontSize: 12.5, textAlign: 'end' }} />
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>jours</span>
        </div>
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '7px 0 0', lineHeight: 1.55 }}>
          C’est l’avance de prévenance, et rien d’autre. L’écran des matériaux s’en sert pour
          deux choses : colorer les flacons dont l’échéance approche, et proposer sur chaque
          ligne la <strong>date de notification</strong> — la péremption saisie moins ces
          jours-là. Tant que cette case reste vide, l’écran travaille sur 60 jours repris des
          lots de réactifs, et continue de dire que le laboratoire ne l’a pas validé.
        </p>
      </div>

      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--text-body)' }}>La durée de vie ne se règle plus ici.</strong> Elle est
          portée par la référence : chaque calibrant et chaque matériau de contrôle porte sa
          <em> péremption après reconstitution / ouverture</em>, saisie avec le reste de la
          référence — elle est imprimée sur le flacon, il n’y a qu’à la recopier. Depuis le
          03/10/2026, c’est la <strong>première</strong> de cette date et de la péremption du lot
          qui fait l’échéance, et l’écran dit laquelle (« fin du lot » ou « fin de stabilité ») ;
          si une seule des deux est saisie, elle fait foi seule et l’autre est signalée manquante.
          Les stabilités des aliquots (congelé, après décongélation) se saisissent de même avec le
          flacon, au geste <em>Reconstituer un flacon</em>.
        </p>
      </div>
      {/* Les ALIQUOTS (03/10/2026) : ne reste ici que ce qui n'est pas une
          propriété du produit — rythme de passage, niveaux, alerte de rupture,
          étiquetage. Les stabilités sont au flacon. */}
      <ReglagesAliquots value={conf} onChange={onChange} />
    </div>
  );
}

/* ══════════════════════ DROITS DU CONTRÔLE QUALITÉ — colonnes ══════════════════════ */

/** Les types d'utilisateur qui peuvent porter un droit sur le module. Les
 *  comptes externes (confrère, public) en sont absents : le serveur refuse de
 *  leur en accorder un, et proposer la case aurait promis ce qu'il refuse. */
const PROFILS_DROITS: { cle: string; label: string }[] = (['technician', 'manager', 'superadmin'] as const)
  .map((cle) => ({ cle, label: LIBELLES_PROFILS[cle].label }));


/* ══════════════════════ DROITS DU CONTRÔLE QUALITÉ (lot E, 09/10/2026) ══════════════════════
 *
 * Douze droits (`cqiSaisir`, `cqiValider`, `cqiDeroger`, … `imprimer`) vivaient
 * dans `settings.perms` depuis le 28/07/2026 SANS AUCUN ÉCRAN : ils ne se
 * réglaient qu'en base. Même tableau que les sections, même convention : Oui ou
 * Non en couleur, la valeur EFFECTIVE (réglage ou défaut) ; basculer vers le
 * défaut RETIRE la clé plutôt que d'écrire un faux — et pour un droit à repli
 * (`cqiApprouverCible`, `cqiRapporter`) l'absence de clé laisse répondre le
 * droit ancien, comme le serveur (`peutDroitOuRepli`).
 */
function DroitsCqPanel({ perms, onChange }: {
  perms: Record<string, Record<string, boolean>>;
  onChange: (perms: Record<string, Record<string, boolean>>) => void;
}) {
  const effectif = (profil: string, cle: string, repli?: string): boolean => (repli ? peutOuRepli(perms, profil, cle, repli) : peut(perms, profil, cle));
  const parDefaut = (profil: string, cle: string, repli?: string): boolean => (repli ? peutOuRepli({}, profil, cle, repli) : peut({}, profil, cle));
  const basculer = (profil: string, cle: string, repli?: string) => {
    const voulu = !effectif(profil, cle, repli);
    const duProfil = { ...(perms?.[profil] || {}) };
    // Revenir au défaut = retirer la clé : un enregistrement ne fige jamais tout le tableau.
    if (voulu === parDefaut(profil, cle, repli) && !repli) delete duProfil[cle]; else duProfil[cle] = voulu;
    onChange({ ...perms, [profil]: duProfil });
  };
  const familles = [...new Set(DROITS_CQ.map((d) => d.famille))];
  return (
    <div style={{ display: 'grid', gap: 8, marginTop: 18 }}>
      <h4 style={{ margin: 0, fontSize: 13.5 }}>Droits du contrôle qualité</h4>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Un acte par ligne, un type d’utilisateur par colonne. Ces droits s’ajoutent aux rubriques ci-dessus : voir la rubrique ne suffit pas pour déroger ou approuver.
        Un droit marqué « repli » suit, tant qu’il n’est pas réglé, le droit nommé entre parenthèses.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table ref={enTeteCollant} className="ged-tableau" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr>
              <th scope="col" style={{ textAlign: 'left', padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', borderBottom: '1px solid var(--border-default)' }}>Acte</th>
              {PROFILS_DROITS.map((p) => <th scope="col" key={p.cle} style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', borderBottom: '1px solid var(--border-default)', textAlign: 'center' }}>{p.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {familles.map((f) => (
              <React.Fragment key={f}>
                <tr><td colSpan={1 + PROFILS_DROITS.length} style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', fontWeight: 700, fontSize: 'var(--tableau-ligne-compacte-police)', textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>{f}</td></tr>
                {DROITS_CQ.filter((d) => d.famille === f).map((d) => (
                  <tr key={d.cle}>
                    <td style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', borderBottom: '1px solid var(--gray-100)' }} title={d.aide}>
                      {d.libelle}
                      <span style={{ marginLeft: 6, fontSize: 11.5, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>{d.cle}</span>
                      {d.repli && <span style={{ marginLeft: 6, fontSize: 12, color: 'var(--cyan-700)' }}>· repli ({d.repli})</span>}
                    </td>
                    {PROFILS_DROITS.map((p) => {
                      const oui = effectif(p.cle, d.cle, d.repli);
                      const regle = typeof perms?.[p.cle]?.[d.cle] === 'boolean';
                      return (
                        <td key={p.cle} style={{ padding: 'var(--tableau-ligne-compacte-padding-y) var(--tableau-ligne-compacte-padding-x)', borderBottom: '1px solid var(--gray-100)', textAlign: 'center' }}>
                          <button type="button" onClick={() => basculer(p.cle, d.cle, d.repli)} aria-pressed={oui}
                            aria-label={`${d.libelle} — ${p.label} : ${oui ? 'oui' : 'non'}`}
                            title={regle ? 'Réglé ici — cliquer pour basculer' : `Valeur par défaut${d.repli ? ` (suit ${d.repli})` : ''} — cliquer pour régler`}
                            style={{ minWidth: 46, height: 24, padding: '0 8px', borderRadius: 'var(--radius-pill)', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                              border: `1px solid ${oui ? 'var(--green-600)' : 'var(--red-600)'}`,
                              background: oui ? 'var(--green-50)' : 'var(--red-50)',
                              color: oui ? 'var(--green-700)' : 'var(--red-700)',
                              opacity: regle ? 1 : 0.75 }}>
                            {oui ? 'Oui' : 'Non'}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Type d'une question du quiz de satisfaction — l'élément du tableau. */
type QuestionQuiz = NonNullable<GedSettings['quizSatisfaction']>['questions'][number];

/**
 * Paramétrage du quiz de satisfaction du panneau « Votre avis » (17/08/2026).
 *
 * L'ordre du tableau EST l'ordre d'affichage sur le site — d'où les flèches,
 * et pas de champ « ordre » à tenir à la main. La borne « 5 questions actives
 * au plus » est celle du serveur (controllers/ged-setting.ts) ; ici on
 * prévient avant l'enregistrement, on n'empêche pas de composer.
 *
 * Le libellé français est le socle : recopié sur chaque retour au dépôt, et
 * servi au site quand la traduction manque. L'arabe et l'anglais sont
 * facultatifs — le site public parle les trois langues.
 */
/** MODÈLE DE QUIZ — trois questions courtes, notées en étoiles.
 *
 *  ── Pourquoi trois, et pourquoi celles-là ───────────────────────────────────
 *
 *  Le quiz accompagne un avis déjà déposé : il est facultatif et se remplit
 *  APRÈS avoir écrit. Trois questions se répondent d'un coup d'œil ; à cinq —
 *  le plafond que le serveur applique — on cesse de lire et on clique au
 *  hasard, ce qui produit des moyennes propres et fausses.
 *
 *  Elles suivent le PARCOURS du patient, dans son ordre vécu : on est accueilli,
 *  on attend, on lit un compte rendu. Chacune porte sur un moment que le
 *  laboratoire peut corriger — et aucune ne demande un jugement global, qui ne
 *  dit jamais quoi reprendre.
 *
 *  ── Étoiles et non oui/non ──────────────────────────────────────────────────
 *
 *  Sur ces trois-là, oui/non écraserait l'information : « le délai a-t-il été
 *  respecté ? » n'a pas de réponse binaire quand il l'a été de justesse. Le
 *  type `note` rend cinq étoiles (★/☆) sur le site.
 *
 *  Les trois traductions sont fournies : le site public parle FR, AR et EN, et
 *  une question sans traduction s'y affiche en français au milieu du reste. */
const QUIZ_MODELE: QuestionQuiz[] = [
  {
    id: 'accueil', type: 'note', actif: true,
    libelle: {
      fr: 'L’accueil et la prise en charge',
      ar: 'الاستقبال والتكفّل',
      en: 'Reception and care',
    },
  },
  {
    id: 'delai', type: 'note', actif: true,
    libelle: {
      fr: 'Le respect du délai annoncé',
      ar: 'احترام الأجل المعلن',
      en: 'Turnaround time as announced',
    },
  },
  {
    id: 'compte-rendu', type: 'note', actif: true,
    libelle: {
      fr: 'La clarté du compte rendu',
      ar: 'وضوح التقرير',
      en: 'Clarity of the report',
    },
  },
];

function QuizParams({ value, onChange }: {
  value: GedSettings['quizSatisfaction'];
  onChange: (v: GedSettings['quizSatisfaction']) => void;
}) {
  const questions = value?.questions ?? [];
  const actives = questions.filter((q) => q.actif !== false).length;
  const sansLibelle = questions.some((q) => !q.libelle?.fr?.trim());
  const delai = Number(value?.delaiReponseJours ?? 0);
  /* ⚠️ Le délai voyage avec les questions : `onChange` REMPLACE l'objet entier,
     et n'écrire que `{ questions }` l'effacerait à chaque modification de
     question — en silence, comme tout ce qui se perd dans un remplacement. */
  const maj = (qs: QuestionQuiz[]) => onChange({ questions: qs, delaiReponseJours: value?.delaiReponseJours });
  const majUne = (i: number, patch: Partial<QuestionQuiz>) =>
    maj(questions.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const deplacer = (i: number, delta: -1 | 1) => {
    const j = i + delta;
    if (j < 0 || j >= questions.length) return;
    const qs = questions.slice();
    const [q] = qs.splice(i, 1);
    qs.splice(j, 0, q as QuestionQuiz);
    maj(qs);
  };

  const btnIc: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 26, height: 26, background: 'var(--surface-card)', border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)', cursor: 'pointer',
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {/* Le délai annoncé sur la vitrine — au-dessus des questions, parce qu'il
          s'applique à TOUT dépôt d'avis, quiz ou pas. */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12.5, fontWeight: 600 }} htmlFor="delaiRq">
          Délai de réponse annoncé
        </label>
        <input id="delaiRq" type="number" min={0} max={90} value={Number.isFinite(delai) ? delai : 0}
          onChange={(e) => onChange({ questions, delaiReponseJours: Number(e.target.value) })}
          style={{ width: 74, padding: '5px 8px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', fontSize: 13 }} />
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>jours ouvrés</span>
      </div>
      <p style={{ margin: '-4px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
        {delai > 0
          ? `Le site affichera « la Responsable Qualité vous recontacte sous ${delai} jours ouvrés » — mais SEULEMENT si le déposant a laissé une adresse. Sans adresse, aucun délai n’est promis.`
          : 'Aucun délai n’est annoncé : le site dira simplement que la Responsable Qualité recontactera le déposant, s’il a laissé une adresse.'}
      </p>
      {questions.length === 0 && (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
          Aucune question : le panneau « Votre avis » du site s’affiche sans quiz.
          C’est l’état de départ — rien n’est posé aux patients tant que rien n’est décidé ici.
        </p>
      )}
      {/* Le modèle est un POINT DE DÉPART, pas un réglage : une fois chargé, il
          se modifie et se retire comme n'importe quelle question. On ne le
          propose plus dès qu'il y a des questions — le recharger écraserait ce
          que le RQ vient de composer. */}
      {questions.length === 0 && (
        <div>
          <Bouton variante="primaire" taille="sm" type="button" onClick={() => maj(QUIZ_MODELE.map((q) => ({ ...q, libelle: { ...q.libelle } })))}>
            <I n="plus" s={13} />Charger le modèle à 3 questions (étoiles)
          </Bouton>
          <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            L’accueil · le délai · la clarté du compte rendu — le parcours du patient dans son ordre vécu,
            noté de 1 à 5 étoiles, en français, arabe et anglais. Modifiable ensuite, question par question.
          </p>
        </div>
      )}

      {questions.map((q, i) => (
        <div key={q.id} style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '12px 14px', background: q.actif !== false ? 'var(--surface-card)' : 'var(--gray-25)', opacity: q.actif !== false ? 1 : 0.75 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>Q{i + 1}</span>
            <select value={q.type} aria-label={`Type de la question ${i + 1}`}
              onChange={(e) => majUne(i, { type: e.target.value as QuestionQuiz['type'] })}
              style={{ ...inputStyle, width: 190, height: 32, fontSize: 12.5 }}>
              <option value="note">Note (1 à 5 étoiles)</option>
              <option value="ouinon">Oui / non</option>
            </select>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-muted)', cursor: 'pointer' }}>
              <input type="checkbox" checked={q.actif !== false} aria-label={`Question ${i + 1} active`}
                onChange={(e) => majUne(i, { actif: e.target.checked })} style={{ accentColor: 'var(--color-primary)' }} />
              posée sur le site
            </label>
            <span style={{ flex: 1 }} />
            <button type="button" onClick={() => deplacer(i, -1)} disabled={i === 0} title="Monter"
              style={{ ...btnIc, opacity: i === 0 ? 0.4 : 1, transform: 'rotate(-90deg)' }}><I n="arrow" s={12} /></button>
            <button type="button" onClick={() => deplacer(i, 1)} disabled={i === questions.length - 1} title="Descendre"
              style={{ ...btnIc, opacity: i === questions.length - 1 ? 0.4 : 1, transform: 'rotate(90deg)' }}><I n="arrow" s={12} /></button>
            <button type="button" onClick={() => maj(questions.filter((_, j) => j !== i))} title="Retirer la question"
              style={{ ...btnIc, color: 'var(--red-600)' }}><I n="x" s={13} /></button>
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            <div>
              <label style={lbl} htmlFor={`quiz-fr-${q.id}`}>Libellé (français) — recopié sur chaque retour</label>
              <input id={`quiz-fr-${q.id}`} value={q.libelle?.fr || ''}
                placeholder="Recommanderiez-vous notre laboratoire ?"
                onChange={(e) => majUne(i, { libelle: { ...q.libelle, fr: e.target.value } })} style={inputStyle} />
            </div>
            <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
              <div>
                <label style={lbl} htmlFor={`quiz-ar-${q.id}`}>Arabe (facultatif)</label>
                <input id={`quiz-ar-${q.id}`} dir="rtl" value={q.libelle?.ar || ''}
                  onChange={(e) => majUne(i, { libelle: { ...q.libelle, ar: e.target.value } })} style={inputStyle} />
              </div>
              <div>
                <label style={lbl} htmlFor={`quiz-en-${q.id}`}>Anglais (facultatif)</label>
                <input id={`quiz-en-${q.id}`} dir="ltr" value={q.libelle?.en || ''}
                  onChange={(e) => majUne(i, { libelle: { ...q.libelle, en: e.target.value } })} style={inputStyle} />
              </div>
            </div>
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Bouton taille="sm" type="button" onClick={() => maj([...questions, {
            // Identifiant STABLE, jamais recalculé : c'est lui qui rattache les
            // réponses déjà déposées à leur question.
            id: `q-${Date.now().toString(36)}${Math.floor(Math.random() * 36).toString(36)}`,
            type: 'note', actif: true, libelle: { fr: '' },
          }])}>
          <I n="plus" s={13} />Ajouter une question
        </Bouton>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {actives} question{actives > 1 ? 's' : ''} active{actives > 1 ? 's' : ''} sur le site
        </span>
      </div>

      {(actives > 5 || sansLibelle) && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          {actives > 5 && <div><strong>{actives} questions actives</strong> — le serveur en refuse plus de 5 : le quiz doit rester léger pour les patients. Désactivez-en avant d’enregistrer.</div>}
          {sansLibelle && <div>Une question sans libellé français sera refusée à l’enregistrement.</div>}
        </div>
      )}
    </div>
  );
}

/* ═══════════════ P1 — le pop-up programmé (annonces) ═══════════════ */

interface AnnonceLigne {
  documentId: string; titre: string; corps: string; aImage: boolean; imageNom: string | null;
  du: string; au: string | null; actif: boolean;
  sondage: { question: string; options: string[] } | null;
  vues: number; reponses: number; resultats: Record<string, number> | null;
  creePar: string; creeLe: string;
}

function AnnoncesParams() {
  const { showToast } = useGed();
  const [lignes, setLignes] = React.useState<AnnonceLigne[] | null>(null);
  const [f, setF] = React.useState({ titre: '', corps: '', du: '', au: '', question: '', options: '', imageNom: '', imageData: '' });
  const [enCours, setEnCours] = React.useState(false);
  const [tour, setTour] = React.useState(0);

  React.useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/annonces', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setLignes(j?.annonces || []); })
      .catch(() => { if (vivant) setLignes([]); });
    return () => { vivant = false; };
  }, [tour]);

  const creer = async () => {
    setEnCours(true);
    try {
      /* Le sondage se saisit en une ligne d'options séparées par « / » : un
         éditeur de listes pour trois humeurs serait plus lourd que le sondage. */
      const options = f.options.split('/').map((x) => x.trim()).filter(Boolean);
      const corps: Record<string, unknown> = {
        titre: f.titre, corps: f.corps, du: f.du, au: f.au || null,
        sondage: f.question.trim() && options.length >= 2 ? { question: f.question.trim(), options } : null,
      };
      if (f.imageData) { corps.imageNom = f.imageNom; corps.imageData = f.imageData; }
      const r = await fetchEcriture('/api/ged/ged/annonces', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { showToast(j?.error?.message || 'Enregistrement refusé', 'erreur'); return; }
      showToast('Annonce programmée.');
      setF({ titre: '', corps: '', du: '', au: '', question: '', options: '', imageNom: '', imageData: '' });
      setTour((t) => t + 1);
    } finally { setEnCours(false); }
  };

  const basculer = async (a: AnnonceLigne) => {
    await fetchEcriture('/api/ged/ged/annonces', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      /* `base` (14/10/2026) : la bascule ne s'applique que si personne n'a
         basculé l'annonce entre-temps — sinon 409, au lieu d'un double clic
         qui défait ce qu'un autre vient de faire. */
      body: JSON.stringify({ documentId: a.documentId, titre: a.titre, du: a.du, au: a.au, actif: !a.actif, base: { titre: a.titre, du: a.du, au: a.au, actif: a.actif } }),
    }).catch(() => undefined);
    setTour((t) => t + 1);
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'grid', gap: 10, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
          <div><label style={lbl} htmlFor="an-titre">Titre</label>
            <input id="an-titre" value={f.titre} onChange={(e) => setF({ ...f, titre: e.target.value })}
              placeholder="Bonne année 2027 !" style={inputStyle} /></div>
          <div><label style={lbl} htmlFor="an-du">Apparaît le</label>
            <input id="an-du" type="date" value={f.du} onChange={(e) => setF({ ...f, du: e.target.value })} style={inputStyle} /></div>
          <div><label style={lbl} htmlFor="an-au">Jusqu’au (vide = un seul jour)</label>
            <input id="an-au" type="date" value={f.au} onChange={(e) => setF({ ...f, au: e.target.value })} style={inputStyle} /></div>
        </div>
        <div><label style={lbl} htmlFor="an-corps">Le mot</label>
          <textarea id="an-corps" value={f.corps} onChange={(e) => setF({ ...f, corps: e.target.value })}
            rows={2} style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label style={lbl} htmlFor="an-q">Mini-sondage — la question (facultatif)</label>
            <input id="an-q" value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })}
              placeholder="L’humeur du jour ?" style={inputStyle} /></div>
          <div><label style={lbl} htmlFor="an-o">Les options, séparées par « / »</label>
            <input id="an-o" value={f.options} onChange={(e) => setF({ ...f, options: e.target.value })}
              placeholder="😊 / 😐 / 😫" style={inputStyle} /></div>
        </div>
        <div>
          <label style={lbl} htmlFor="an-img">Photo (JPG, PNG, WEBP — 2 Mo au plus)</label>
          <input id="an-img" type="file" accept="image/*" style={{ fontSize: 12.5 }}
            onChange={(e) => {
              const fichier = e.target.files?.[0];
              if (!fichier) { setF((x) => ({ ...x, imageNom: '', imageData: '' })); return; }
              const lecteur = new FileReader();
              lecteur.onload = () => setF((x) => ({ ...x, imageNom: fichier.name, imageData: String(lecteur.result || '') }));
              lecteur.readAsDataURL(fichier);
            }} />
        </div>
        <Bouton variante="primaire" taille="sm" type="button" disabled={enCours || !f.titre.trim() || !f.du} onClick={() => void creer()} style={{ justifySelf: 'start' }}>
          Programmer l’annonce
        </Bouton>
      </div>

      {lignes === null ? <Skeleton rows={2} /> : !lignes.length ? (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
          Aucune annonce programmée — la première apparaîtra ici, avec ses résultats.
        </p>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {lignes.map((a) => (
            <div key={a.documentId} style={{ display: 'grid', gap: 4, padding: '10px 12px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', opacity: a.actif ? 1 : 0.6 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 13 }}>{a.titre}</strong>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  {a.du}{a.au ? ` → ${a.au}` : ''}
                </span>
                {a.aImage && <span style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>photo</span>}
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{a.vues} vue(s)</span>
                <span style={{ flex: 1 }} />
                <Bouton taille="sm" type="button" onClick={() => void basculer(a)}>
                  {a.actif ? 'Désactiver' : 'Réactiver'}
                </Bouton>
              </div>
              {a.sondage && a.resultats && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12.5 }}>
                  <span style={{ color: 'var(--text-muted)' }}>{a.sondage.question}</span>
                  {Object.entries(a.resultats).map(([o, n]) => (
                    <span key={o} style={{ fontFamily: 'var(--font-mono)' }}>
                      {o} <strong>{n}</strong>
                    </span>
                  ))}
                  <span style={{ color: 'var(--text-faint)' }}>({a.reponses}/{a.vues} ont répondu — compté, jamais nommé)</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
